import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useGlobalFilters } from '../contexts/FilterContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Popover,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  Settings,
  FilterAlt,
  FilterAltOutlined,
  Edit as EditIcon,
  Add as AddIcon,
  Close,
  DeleteOutline,
  Search,
  KeyboardArrowUp,
  Warning,
} from '@mui/icons-material';
import DecommissionTrends from './DecommissionTrends';
import DecomBowlerMetrics from './DecomBowlerMetrics';
import { API_BASE_URL, DCO, INCIDENTS, RELIABILITY_FRAMEWORK } from '../config/api';
import { authFetch } from '../services/authFetch';

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface DecommissioningDataResponse {
  success: boolean;
  data: DecommissioningItem[];
  total_records: number;
  filters: { portfolio: string | null; platform: string | null };
  timestamp: string;
}

interface StrategicIntent { id: string; intent: string }
interface RevisionValue { newValue: string; oldValue: string; fieldName: string }
interface Revision { values: RevisionValue[]; modifiedBy: string; modifiedDate: string; revisionType: string }
interface IncidentApiResponse {
  app_id?: string;
  cmdb_ci_u_application_id?: string;
  priority?: string;
  portfolio_name?: string;
  platform_name?: string;
}

interface DecommissioningItem {
  portfolio: string;
  platform: string;
  app_id: string;
  app_name: string;
  mvc_group?: string | null;
  decom_change_description: string | null;
  target_date_decom: string | null;
  decom_date: string | null;
  decom_status: number;
  owner_names: string;
  owners: string;
  status: string;
  decom_extension_reason: string | null;
  strategic_intent: StrategicIntent | null;
  backup_tested_last_12_months: string | null;
  failover_tested_date_last_12_months: string | null;
  total_cost: number | null;
  itfm_total_cost: number | null;
  cip_direct_cost?: number | null;
  cip_indirect_cost?: number | null;
  cip_month?: string | null;
  bia_status: boolean | null;
  erm_tdd: string | null;
  risk_exposure_all?: number | null;
  risk_exposure_h?: number | null;
  application_labels: Array<{ color: string; description: string; id: number; name: string }> | null;
  p1_count?: number | null;
  p2_count?: number | null;
  p3_count?: number | null;
  p4_count?: number | null;
  suspended?: boolean | null;
  revisions?: Revision[];
  tag?: {
    id: number;
    decomm_tag: string | null;
    remarks: string;
    comments: string | null;
    created_by: string;
    updated_by: string;
    created_at: string;
    updated_at: string;
  } | null;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DCO_ALLOWED_USERS: string[] = [
  'k.harish@maersk.com',
  'shilpitha.swarna@maersk.com',
  'nilesh.chakkar@maersk.com',
  'nitish.yn@maersk.com',
  'girish.kumsi@maersk.com',
  'mahesh.s.bhosale@maersk.com',
  'paramjeet.nagpal@maersk.com',
  'vaishali.parande@maersk.com',
];

const TAG_AUTHORIZED_USERS: string[] = [
  'mahesh.s.bhosale@maersk.com',
  'shilpitha.swarna@maersk.com',
  'nilesh.chakkar@maersk.com',
  'girish.kumsi@maersk.com',
  'paramjeet.nagpal@maersk.com',
  'vaishali.parande@maersk.com',
  'ivy.serafica.belarmino@maersk.com',
];

// ─── Reusable filter sub-components (defined OUTSIDE main component to prevent remount blinking) ─────

const CheckboxListPopover = ({ anchor, onClose, title, searchText, onSearchChange, items, selected, onToggle, onClear, onApply, hideSearch }: any) => (
  <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 280, maxHeight: 420, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}>
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>{title}</Typography>
      {!hideSearch && (
        <TextField fullWidth size="small" placeholder="Search" value={searchText} onChange={e => onSearchChange(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />, endAdornment: searchText ? <IconButton size="small" onClick={() => onSearchChange('')} sx={{ padding: 0 }}><Close sx={{ fontSize: 16 }} /></IconButton> : null }}
          sx={{ mb: 1 }} />
      )}
      <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#fff', mb: 1 }}>
        {items.map((item: string) => (
          <Box key={item} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }} onClick={() => onToggle(item)}>
            <Checkbox checked={selected.includes(item)} size="small" sx={{ padding: '4px' }} />
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClear} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0', bgcolor: '#fff' }}>Clear</Button>
        <Button size="small" variant="contained" onClick={onApply} sx={{ textTransform: 'none', bgcolor: '#0a3055' }}>Apply</Button>
      </Box>
    </Box>
  </Popover>
);

const DateFilterPopover = ({ anchor, onClose, tempOperator, setTempOperator, tempFilter, setTempFilter, tempCustomStart, setTempCustomStart, tempCustomEnd, setTempCustomEnd, onClear, onApply }: any) => (
  <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 320, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}>
    <Box sx={{ p: 2 }}>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select value={tempOperator} onChange={e => setTempOperator(e.target.value)} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' } }}>
          <MenuItem value="equals">Equals</MenuItem>
          <MenuItem value="after">After</MenuItem>
          <MenuItem value="before">Before</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>
      </FormControl>
      {tempOperator === 'custom' ? (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>Start Date</Typography>
          <TextField fullWidth size="small" type="date" value={tempCustomStart} onChange={e => setTempCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>End Date</Typography>
          <TextField fullWidth size="small" type="date" value={tempCustomEnd} onChange={e => setTempCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
        </Box>
      ) : (
        <TextField fullWidth size="small" type="date" value={tempFilter} onChange={e => setTempFilter(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClear} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0' }}>Clear</Button>
        <Button size="small" variant="contained" onClick={onApply} sx={{ textTransform: 'none', bgcolor: '#0a3055' }}>Apply</Button>
      </Box>
    </Box>
  </Popover>
);

// ─── Component ───────────────────────────────────────────────────────────────

const DecommissioningStatus: React.FC = () => {
  const { accounts } = useMsal();
  const currentUserEmail = ((accounts[0] as any)?.username || (accounts[0] as any)?.idTokenClaims?.preferred_username || '').toLowerCase();
  const { selectedPortfolio, setSelectedPortfolio, selectedPlatform, setSelectedPlatform } = useGlobalFilters();

  // ── Portfolio / Platform options ───────────────────────────────────────────
  const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);
  const [allPortfolioData, setAllPortfolioData] = useState<{ platform_portfolio: string; platform: string }[]>([]);

  // Fetch portfolio/platform options from the same source as Portfolio Health Tracker
  useEffect(() => {
    authFetch(RELIABILITY_FRAMEWORK.PORTFOLIO_OVERVIEW)
      .then(r => r.json())
      .then(result => {
        const data: any[] = result.data || result;
        if (!Array.isArray(data)) return;
        setAllPortfolioData(data);
        const portfolios = Array.from(new Set(data.map((i: any) => i.platform_portfolio).filter(Boolean))) as string[];
        setPortfolioOptions(portfolios.sort());
        const allPlatforms = Array.from(new Set(data.map((i: any) => i.platform).filter(Boolean))) as string[];
        setPlatformOptions(allPlatforms.sort());
      })
      .catch(() => { });
  }, []);

  // Filter platforms when portfolio changes
  useEffect(() => {
    if (!allPortfolioData.length) return;
    if (selectedPortfolio && selectedPortfolio !== 'all') {
      const filtered = Array.from(
        new Set(allPortfolioData.filter((i: any) => i.platform_portfolio === selectedPortfolio).map((i: any) => i.platform).filter(Boolean))
      ) as string[];
      setPlatformOptions(filtered.sort());
      if (selectedPlatform !== 'all' && !filtered.includes(selectedPlatform)) {
        setSelectedPlatform('all');
      }
    } else {
      const all = Array.from(new Set(allPortfolioData.map((i: any) => i.platform).filter(Boolean))) as string[];
      setPlatformOptions(all.sort());
    }
  }, [selectedPortfolio, allPortfolioData, selectedPlatform, setSelectedPlatform]);

  const canManageDcoCsv = !!currentUserEmail && DCO_ALLOWED_USERS.some(u => u.toLowerCase() === currentUserEmail);
  const isUserAuthorizedForTags = () => TAG_AUTHORIZED_USERS.some(e => e.toLowerCase() === currentUserEmail);

  // ── View ───────────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const initialView: 'table' | 'trends' | 'bowler' =
    viewParam === 'trends' || viewParam === 'bowler' ? viewParam : 'table';
  const [decomView, setDecomView] = useState<'table' | 'trends' | 'bowler'>(initialView);

  const handleDecomViewChange = (v: 'table' | 'trends' | 'bowler') => {
    setDecomView(v);
    if (v === 'table') {
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ ...Object.fromEntries(searchParams.entries()), view: v }, { replace: true });
    }
  };

  // ── Data ───────────────────────────────────────────────────────────────────
  const [decommissioningData, setDecommissioningData] = useState<DecommissioningItem[]>([]);
  const [decommissioningLoading, setDecommissioningLoading] = useState(true);
  const [decommissioningError, setDecommissioningError] = useState<string | null>(null);
  const [p1p2IncidentData, setP1P2IncidentData] = useState<IncidentApiResponse[]>([]);
  const [p3p4IncidentData, setP3P4IncidentData] = useState<IncidentApiResponse[]>([]);

  // ── Pagination & Sort ──────────────────────────────────────────────────────
  const [decomPage, setDecomPage] = useState(0);
  const [decomRowsPerPage, setDecomRowsPerPage] = useState(10);
  const [decomSortConfig, setDecomSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [decomSearchQuery, setDecomSearchQuery] = useState('');
  const [decomTrackingFilter, setDecomTrackingFilter] = useState<'all' | 'completed' | 'ontrack' | 'delayed' | 'awaiting' | 'cancelled'>('all');

  // ── DCO CSV ────────────────────────────────────────────────────────────────
  const [dcoCsvLoading, setDcoCsvLoading] = useState(false);
  const dcoCsvAbortControllerRef = useRef<AbortController | null>(null);

  // ── Portfolio filter ───────────────────────────────────────────────────────
  const [decomPortfolioFilter, setDecomPortfolioFilter] = useState<string[]>([]);
  const [decomPortfolioTempFilter, setDecomPortfolioTempFilter] = useState<string[]>([]);
  const [decomPortfolioFilterAnchor, setDecomPortfolioFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPortfolioSearchText, setDecomPortfolioSearchText] = useState('');

  // ── Platform filter ────────────────────────────────────────────────────────
  const [decomPlatformFilter, setDecomPlatformFilter] = useState<string[]>([]);
  const [decomPlatformTempFilter, setDecomPlatformTempFilter] = useState<string[]>([]);
  const [decomPlatformFilterAnchor, setDecomPlatformFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPlatformSearchText, setDecomPlatformSearchText] = useState('');

  // ── Strategic Intent filter ────────────────────────────────────────────────
  const [decomStrategicIntentFilter, setDecomStrategicIntentFilter] = useState<string[]>([]);
  const [decomStrategicIntentTempFilter, setDecomStrategicIntentTempFilter] = useState<string[]>([]);
  const [decomStrategicIntentFilterAnchor, setDecomStrategicIntentFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomStrategicIntentSearchText, setDecomStrategicIntentSearchText] = useState('');

  // ── MAC Status filter ──────────────────────────────────────────────────────
  const [decomStatusFilter, setDecomStatusFilter] = useState<string[]>([]);
  const [decomStatusTempFilter, setDecomStatusTempFilter] = useState<string[]>([]);
  const [decomStatusFilterAnchor, setDecomStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomStatusSearchText, setDecomStatusSearchText] = useState('');

  // ── Decom Status filter ────────────────────────────────────────────────────
  const [decomDecomStatusFilter, setDecomDecomStatusFilter] = useState<string[]>([]);
  const [decomDecomStatusTempFilter, setDecomDecomStatusTempFilter] = useState<string[]>([]);
  const [decomDecomStatusFilterAnchor, setDecomDecomStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomDecomStatusSearchText, setDecomDecomStatusSearchText] = useState('');

  // ── Application Labels filter ──────────────────────────────────────────────
  const [decomApplicationLabelsFilter, setDecomApplicationLabelsFilter] = useState<string[]>([]);
  const [decomApplicationLabelsTempFilter, setDecomApplicationLabelsTempFilter] = useState<string[]>([]);
  const [decomApplicationLabelsFilterAnchor, setDecomApplicationLabelsFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomApplicationLabelsSearchText, setDecomApplicationLabelsSearchText] = useState('');

  // ── Owners filter ──────────────────────────────────────────────────────────
  const [decomOwnersFilter, setDecomOwnersFilter] = useState('');
  const [decomOwnersTempFilter, setDecomOwnersTempFilter] = useState('');
  const [decomOwnersFilterAnchor, setDecomOwnersFilterAnchor] = useState<null | HTMLElement>(null);

  // ── MVC Group filter ─────────────────────────────────────────────────────
  const [decomMvcGroupFilter, setDecomMvcGroupFilter] = useState<string[]>([]);
  const [decomMvcGroupTempFilter, setDecomMvcGroupTempFilter] = useState<string[]>([]);
  const [decomMvcGroupFilterAnchor, setDecomMvcGroupFilterAnchor] = useState<null | HTMLElement>(null);

  // ── Delayed Comments filter ────────────────────────────────────────────────
  const [decomDelayedCommentsFilter, setDecomDelayedCommentsFilter] = useState('');
  const [decomDelayedCommentsTempFilter, setDecomDelayedCommentsTempFilter] = useState('');
  const [decomDelayedCommentsFilterAnchor, setDecomDelayedCommentsFilterAnchor] = useState<null | HTMLElement>(null);

  // ── Year filter ────────────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const nextYear = currentYear + 1;
  const [decomYearFilter, setDecomYearFilter] = useState<string>(currentYear.toString());
  const [decomYearFilterAnchor, setDecomYearFilterAnchor] = useState<null | HTMLElement>(null);
  const [allYearsExpanded, setAllYearsExpanded] = useState(false);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [yearSearchText, setYearSearchText] = useState('');
  const [customRangeExpanded, setCustomRangeExpanded] = useState(false);
  const [customRangeStart, setCustomRangeStart] = useState('');
  const [customRangeEnd, setCustomRangeEnd] = useState('');

  // ── Prev Target Date filter ────────────────────────────────────────────────
  const [decomPrevTargetDateFilter, setDecomPrevTargetDateFilter] = useState('');
  const [decomPrevTargetDateTempFilter, setDecomPrevTargetDateTempFilter] = useState('');
  const [decomPrevTargetDateFilterAnchor, setDecomPrevTargetDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPrevTargetDateOperator, setDecomPrevTargetDateOperator] = useState<'equals' | 'after' | 'before' | 'custom' | 'allyears'>('equals');
  const [decomPrevTargetDateTempOperator, setDecomPrevTargetDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom' | 'allyears'>('equals');
  const [decomPrevTargetDateCustomStart, setDecomPrevTargetDateCustomStart] = useState('');
  const [decomPrevTargetDateCustomEnd, setDecomPrevTargetDateCustomEnd] = useState('');
  const [decomPrevTargetDateTempCustomStart, setDecomPrevTargetDateTempCustomStart] = useState('');
  const [decomPrevTargetDateTempCustomEnd, setDecomPrevTargetDateTempCustomEnd] = useState('');
  // All-Years sub-filter for Prev Target Date
  const [decomPrevTddSelectedYears, setDecomPrevTddSelectedYears] = useState<string[]>([]);
  const [decomPrevTddTempSelectedYears, setDecomPrevTddTempSelectedYears] = useState<string[]>([]);
  const [decomPrevTddYearSearch, setDecomPrevTddYearSearch] = useState('');

  // ── Decom Date filter ──────────────────────────────────────────────────────
  const [decomDecomDateFilter, setDecomDecomDateFilter] = useState('');
  const [decomDecomDateTempFilter, setDecomDecomDateTempFilter] = useState('');
  const [decomDecomDateFilterAnchor, setDecomDecomDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomDecomDateOperator, setDecomDecomDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomDecomDateTempOperator, setDecomDecomDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomDecomDateCustomStart, setDecomDecomDateCustomStart] = useState('');
  const [decomDecomDateCustomEnd, setDecomDecomDateCustomEnd] = useState('');
  const [decomDecomDateTempCustomStart, setDecomDecomDateTempCustomStart] = useState('');
  const [decomDecomDateTempCustomEnd, setDecomDecomDateTempCustomEnd] = useState('');

  // ── Failover Tested Date filter ────────────────────────────────────────────
  const [decomFailoverTestedDateFilter, setDecomFailoverTestedDateFilter] = useState('');
  const [decomFailoverTestedDateTempFilter, setDecomFailoverTestedDateTempFilter] = useState('');
  const [decomFailoverTestedDateFilterAnchor, setDecomFailoverTestedDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomFailoverTestedDateOperator, setDecomFailoverTestedDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomFailoverTestedDateTempOperator, setDecomFailoverTestedDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomFailoverTestedDateCustomStart, setDecomFailoverTestedDateCustomStart] = useState('');
  const [decomFailoverTestedDateCustomEnd, setDecomFailoverTestedDateCustomEnd] = useState('');
  const [decomFailoverTestedDateTempCustomStart, setDecomFailoverTestedDateTempCustomStart] = useState('');
  const [decomFailoverTestedDateTempCustomEnd, setDecomFailoverTestedDateTempCustomEnd] = useState('');

  // ── Backup Tested Date filter ──────────────────────────────────────────────
  const [decomBackupTestedDateFilter, setDecomBackupTestedDateFilter] = useState('');
  const [decomBackupTestedDateTempFilter, setDecomBackupTestedDateTempFilter] = useState('');
  const [decomBackupTestedDateFilterAnchor, setDecomBackupTestedDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomBackupTestedDateOperator, setDecomBackupTestedDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomBackupTestedDateTempOperator, setDecomBackupTestedDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomBackupTestedDateCustomStart, setDecomBackupTestedDateCustomStart] = useState('');
  const [decomBackupTestedDateCustomEnd, setDecomBackupTestedDateCustomEnd] = useState('');
  const [decomBackupTestedDateTempCustomStart, setDecomBackupTestedDateTempCustomStart] = useState('');
  const [decomBackupTestedDateTempCustomEnd, setDecomBackupTestedDateTempCustomEnd] = useState('');

  // ── BIA Status filter ──────────────────────────────────────────────────────
  const [decomBiaStatusFilter, setDecomBiaStatusFilter] = useState<string[]>([]);
  const [decomBiaStatusTempFilter, setDecomBiaStatusTempFilter] = useState<string[]>([]);
  const [decomBiaStatusFilterAnchor, setDecomBiaStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomSuspendedFilter, setDecomSuspendedFilter] = useState<string[]>([]);
  const [decomSuspendedTempFilter, setDecomSuspendedTempFilter] = useState<string[]>([]);
  const [decomSuspendedFilterAnchor, setDecomSuspendedFilterAnchor] = useState<null | HTMLElement>(null);

  // ── Column visibility ──────────────────────────────────────────────────────
  const [decomColumnVisibility, setDecomColumnVisibility] = useState({
    portfolio: true, platform: true, app_id: true, app_name: true, mvc_group: false,
    owners: true, target_date: true, prev_target_date: true, decom_date: true,
    delayed_comments: false, strategic_intent: false, backup_tested: false,
    failover_tested: false, status: true, decom_status: true, tag: false,
    application_labels: false, p1p2: false, p3p4mi: false, cip_direct_cost: false, cip_indirect_cost: false, total_cost: false, bia_status: false, cyber_risk: false, suspended: false,
  });
  const [decomColumnDialogOpen, setDecomColumnDialogOpen] = useState(false);

  // ── Tag management ─────────────────────────────────────────────────────────
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTagApp, setSelectedTagApp] = useState<DecommissioningItem | null>(null);
  const [tagFormData, setTagFormData] = useState({ decomm_tag: '', remarks: '', comments: '' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagDeleteLoading, setTagDeleteLoading] = useState(false);
  const [tagSuccessMessage, setTagSuccessMessage] = useState('');
  const [lastDeletedTag, setLastDeletedTag] = useState<{ appId: string; tag: any } | null>(null);


  // ─── Helper functions ─────────────────────────────────────────────────────

  const getAvailableYears = useMemo(() => {
    return Array.from(new Set(
      decommissioningData
        .filter(i => i.target_date_decom)
        .map(i => new Date(i.target_date_decom!).getFullYear())
    )).sort((a, b) => a - b);
  }, [decommissioningData]);

  const getAvailablePrevTddYears = useMemo(() => {
    const years = new Set<number>();
    decommissioningData.forEach(item => {
      if (!item.revisions?.length) return;
      item.revisions.forEach((rev: any) => {
        rev.values?.forEach((val: any) => {
          if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') {
            const d = new Date(val.oldValue);
            if (!isNaN(d.getTime())) years.add(d.getFullYear());
          }
        });
      });
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [decommissioningData]);

  const getAvailableMvcGroups = useMemo(() => {
    return Array.from(new Set(
      decommissioningData.map(i => (i.mvc_group || 'N/A'))
    )).sort();
  }, [decommissioningData]);

  const getOldestPrevTargetDate = useCallback((item: DecommissioningItem): Date | null => {
    if (!item.revisions?.length) return null;
    let oldest: Date | null = null;
    item.revisions.forEach(rev => rev.values.forEach(val => {
      if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') {
        const d = new Date(val.oldValue);
        if (!oldest || d < oldest) oldest = d;
      }
    }));
    return oldest;
  }, []);

  /**
   * Returns all previous TDD values (oldValue) for revisions whose modifiedDate
   * falls within the given year, sorted chronologically (oldest first).
   * Used to show all date changes in the selected year in the Prev Target Date column.
   */
  const getAllPrevTargetDates = useCallback((item: DecommissioningItem): Date[] => {
    if (!item.revisions?.length) return [];
    const dates: { revDate: Date; oldVal: Date }[] = [];
    item.revisions.forEach(rev => {
      rev.values.forEach(val => {
        if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') {
          dates.push({ revDate: new Date(rev.modifiedDate), oldVal: new Date(val.oldValue) });
        }
      });
    });
    dates.sort((a, b) => a.revDate.getTime() - b.revDate.getTime());
    return dates.map(d => d.oldVal);
  }, []);

  const countPrevTargetDates = useCallback((item: DecommissioningItem): number => {
    if (!item.revisions?.length) return 0;
    let count = 0;
    item.revisions.forEach(rev => rev.values.forEach(val => {
      if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') count++;
    }));
    return count;
  }, []);

  const hadPreviousEliminateOrMigrateIntent = useCallback((item: DecommissioningItem): boolean => {
    if (!item.revisions?.length) return false;
    const currentIntent = item.strategic_intent?.intent?.toLowerCase() || '';
    for (const rev of item.revisions) {
      for (const val of rev.values) {
        if ((val.fieldName === 'strategicIntent' || val.fieldName === 'strategic_intent') && val.oldValue && val.oldValue !== 'null') {
          const oldIntent = val.oldValue.toLowerCase();
          if ((oldIntent === 'eliminate' || oldIntent === 'migrate') &&
            (currentIntent !== 'eliminate' && currentIntent !== 'migrate')) {
            return true;
          }
        }
      }
    }
    return false;
  }, []);

  const calculateDecommissioningStatus = useCallback((item: DecommissioningItem): string => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (item.status === 'Inactive') return 'Decommissioned';

    const prevCount = countPrevTargetDates(item);
    const hadPrevIntent = hadPreviousEliminateOrMigrateIntent(item);
    const targetEverExisted = !!item.target_date_decom || prevCount > 0;

    if (targetEverExisted && (hadPrevIntent || !item.target_date_decom)) return 'Cancelled';

    if (item.status === 'Active' && item.target_date_decom) {
      const targetDate = new Date(item.target_date_decom);
      const oldestPrev = getOldestPrevTargetDate(item);
      const hasPrevInPast = oldestPrev && oldestPrev < today;
      // Aligned with Bowler: flag as Delayed if date was pushed out, even when both dates are future
      const dateWasPushedOut = oldestPrev && targetDate > oldestPrev;

      if (targetDate >= today && !hasPrevInPast && !dateWasPushedOut) return 'To be Decommissioned';

      if (dateWasPushedOut || hasPrevInPast) return 'Delayed';

      // Overdue with no extension set — mirrors Bowler "Awaiting Decomm Confirmation"
      if (targetDate < today && !oldestPrev) return 'Awaiting Decomm Confirmation';

      if (targetDate < today) return 'Delayed';
    }
    return 'Unknown';
  }, [countPrevTargetDates, hadPreviousEliminateOrMigrateIntent, getOldestPrevTargetDate]);

  const getIncidentCounts = useCallback((appId: string): { p1: number; p2: number; p3p4mi: number } => {
    const p1p2AppIncidents = p1p2IncidentData.filter(incident =>
      (incident.cmdb_ci_u_application_id || incident.app_id) === appId
    );
    const p3p4AppIncidents = p3p4IncidentData.filter(incident =>
      (incident.cmdb_ci_u_application_id || incident.app_id) === appId &&
      (incident.priority === '3' || incident.priority === '4')
    );

    return {
      p1: p1p2AppIncidents.filter(incident => incident.priority === '1').length,
      p2: p1p2AppIncidents.filter(incident => incident.priority === '2').length,
      p3p4mi: p3p4AppIncidents.length,
    };
  }, [p1p2IncidentData, p3p4IncidentData]);

  // ─── Data fetching ────────────────────────────────────────────────────────

  const fetchDecommissioningData = useCallback(async () => {
    setDecommissioningLoading(true);
    setDecommissioningError(null);
    try {
      // Always fetch all data — portfolio/platform filtering is done client-side
      const url = `${API_BASE_URL}/api/reliability-framework/decommissioning-data`;
      const response = await authFetch(url);
      if (!response.ok) throw new Error(`API returned status ${response.status}`);
      const result: DecommissioningDataResponse = await response.json();

      const tagsResponse = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/?t=${Date.now()}`);
      let tagsData: any[] = [];
      if (tagsResponse.ok) {
        const tagsResult = await tagsResponse.json();
        if (tagsResult.success && tagsResult.data) tagsData = tagsResult.data;
      }

      const mergedData = (result.data || []).map((item: any) => {
        // Only consider non-deleted tags when choosing the latest tag to display
        // Consider all tags for the app and pick the most recent one; if the latest has status 'tag deleted', treat as no tag
        const appTags = tagsData.filter((t: any) => t.app_id === item.app_id);
        const latestTag = appTags.length > 0
          ? appTags.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
          : null;
        const effectiveTag = latestTag && latestTag.status === 'tag deleted' ? null : latestTag;
        return {
          ...item,
          tag: effectiveTag ? {
            id: effectiveTag.id,
            decomm_tag: effectiveTag.decomm_tag,
            remarks: effectiveTag.comments,
            comments: effectiveTag.comments,
            created_by: effectiveTag.created_by,
            updated_by: effectiveTag.updated_by,
            created_at: effectiveTag.created_at,
            updated_at: effectiveTag.updated_at,
          } : null,
        };
      }) as DecommissioningItem[];

      setDecommissioningData(mergedData);
    } catch (err: any) {
      setDecommissioningError(err.message || 'Unknown error');
      setDecommissioningData([]);
    } finally {
      setDecommissioningLoading(false);
    }
  }, []); // no filter deps — data is fetched once; portfolio/platform filtered client-side

  const fetchIncidentCounts = useCallback(async () => {
    try {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const requestBody = {
        start_date: startOfYear.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
        page: 1,
        pageSize: 20000,
      };

      const [p1Response, p3Response] = await Promise.all([
        authFetch(INCIDENTS.P1_P2_WITH_TRENDS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }),
        authFetch(INCIDENTS.P3_P4_WITH_TRENDS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }),
      ]);

      if (p1Response.ok) {
        const p1Result = await p1Response.json();
        const p1Data = p1Result?.data?.incidents || p1Result?.data?.incidents || [];
        setP1P2IncidentData(Array.isArray(p1Data) ? p1Data : []);
      } else {
        setP1P2IncidentData([]);
      }

      if (p3Response.ok) {
        const p3Result = await p3Response.json();
        const p3Data = p3Result?.data?.incidents || p3Result?.data?.incidents || [];
        setP3P4IncidentData(Array.isArray(p3Data) ? p3Data : []);
      } else {
        setP3P4IncidentData([]);
      }
    } catch (err) {
      console.error('Error fetching incident counts for decommissioning status:', err);
      setP1P2IncidentData([]);
      setP3P4IncidentData([]);
    }
  }, []);

  useEffect(() => { fetchDecommissioningData(); }, [fetchDecommissioningData]);
  useEffect(() => { fetchIncidentCounts(); }, [fetchIncidentCounts]);

  // ── Live MSA failover / restore dates ─────────────────────────────────────
  // MSA is the authoritative source; DB values from the decommissioning endpoint are not used.
  const [msaLookup, setMsaLookup] = useState<Record<string, { failover: string | null; restore: string | null }>>({});
  const [msaLoaded, setMsaLoaded] = useState(false);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/api/resiliency-msa`)
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        if (!json?.data) { setMsaLoaded(true); return; }
        const lookup: Record<string, { failover: string | null; restore: string | null }> = {};
        for (const rec of (json.data as Array<{ app_id?: string; failover_tested_date_last_12_months?: string; restore_from_backup_tested_last_12_months?: string }>)) {
          if (rec.app_id) {
            lookup[rec.app_id] = {
              failover: rec.failover_tested_date_last_12_months ?? null,
              restore: rec.restore_from_backup_tested_last_12_months ?? null,
            };
          }
        }
        setMsaLookup(lookup);
        setMsaLoaded(true);
      })
      .catch(() => { setMsaLoaded(true); });
  }, []);

  // Merge live MSA dates; always null out DB values — never fall back to stale DB data.
  const enrichedDecommissioningData = useMemo((): DecommissioningItem[] => {
    return decommissioningData.map(item => {
      const msa = msaLoaded ? msaLookup[item.app_id] : undefined;
      return {
        ...item,
        failover_tested_date_last_12_months: msa ? (msa.failover ?? null) : null,
        backup_tested_last_12_months: msa ? (msa.restore ?? null) : null,
      };
    });
  }, [decommissioningData, msaLookup, msaLoaded]);

  // ─── Tag handlers ─────────────────────────────────────────────────────────

  const handleAddTag = (app: DecommissioningItem) => {
    if (!isUserAuthorizedForTags()) { alert('You are not authorized to manage tags.'); return; }
    setSelectedTagApp(app);
    setTagFormData({ decomm_tag: '', remarks: '', comments: '' });
    setIsEditMode(false);
    setTagModalOpen(true);
  };

  const handleEditTag = (app: DecommissioningItem) => {
    if (!isUserAuthorizedForTags()) { alert('You are not authorized to manage tags.'); return; }
    setSelectedTagApp(app);
    setTagFormData({ decomm_tag: app.tag?.decomm_tag || '', remarks: app.tag?.comments || '', comments: app.tag?.comments || '' });
    setIsEditMode(true);
    setTagModalOpen(true);
  };

  const handleSaveTag = async () => {
    if (!selectedTagApp || !isUserAuthorizedForTags()) return;
    setTagLoading(true);
    try {
      const existingTag = isEditMode ? selectedTagApp.tag : null;
      const tagData = {
        app_id: selectedTagApp.app_id,
        app_name: selectedTagApp.app_name,
        decomm_tag: tagFormData.decomm_tag || existingTag?.decomm_tag || '',
        remarks: tagFormData.remarks || existingTag?.remarks || '',
        comments: '',
        status: isEditMode ? 'tag updated' : 'tag added',
        created_by: currentUserEmail,
        updated_by: currentUserEmail,
      };
      let response;
      if (isEditMode && selectedTagApp?.tag?.id) {
        // Use simple body-based update endpoint (avoid large-id path issues)
        response = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: selectedTagApp.tag.id, ...tagData, updated_by: currentUserEmail }),
        });
      } else {
        response = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tagData),
        });
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.success && result.data) {
        setDecommissioningData(prev => prev.map(item =>
          item.app_id === selectedTagApp.app_id
            ? { ...item, tag: { id: result.data.id || Date.now(), decomm_tag: tagData.decomm_tag || null, remarks: tagData.remarks || '', comments: tagData.remarks || null, created_by: tagData.created_by, updated_by: tagData.updated_by, created_at: result.data.created_at || new Date().toISOString(), updated_at: result.data.created_at || new Date().toISOString() } }
            : item
        ));
      }
      // Close modal first, then show success snackbar
      handleCloseTagModal();
      setTagSuccessMessage(isEditMode ? 'Tag updated successfully!' : 'Tag created successfully!');
    } catch (error) {
      alert('Failed to save tag. Please try again.');
    } finally {
      setTagLoading(false);
    }
  };

  const handleCloseTagModal = () => {
    setTagModalOpen(false);
    setSelectedTagApp(null);
    setTagFormData({ decomm_tag: '', remarks: '', comments: '' });
    setIsEditMode(false);
  };

  const handleDeleteTag = async () => {
    if (!selectedTagApp) return;
    if (!isUserAuthorizedForTags()) { alert('You are not authorized to manage tags.'); return; }
    // Confirm destructive action
    if (!window.confirm('Are you sure you want to delete this tag for this application?')) return;
    setTagDeleteLoading(true);
    const prevData = decommissioningData;
    try {
      if (selectedTagApp.tag) {
        // Call API first — modal stays open with "Deleting..." on button
        const resp = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: selectedTagApp.tag.id, app_id: selectedTagApp.app_id, changed_by: currentUserEmail }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      }
      // API succeeded — update UI, close modal, show success
      setLastDeletedTag({ appId: selectedTagApp.app_id, tag: selectedTagApp.tag });
      setDecommissioningData(prev => prev.map(item => item.app_id === selectedTagApp.app_id ? { ...item, tag: null } : item));
      handleCloseTagModal();
      setTagSuccessMessage('Tag deleted successfully!');
    } catch (err) {
      // Revert on error
      setDecommissioningData(prevData);
      alert('Failed to remove tag. Please try again.');
    } finally {
      setTagDeleteLoading(false);
    }
  };

  // ─── Filtered + sorted data ───────────────────────────────────────────────

  const buildFilteredData = useCallback(() => {
    let data = enrichedDecommissioningData.filter(i => i.target_date_decom);
    data = data.filter(i => i.status === 'Active' || i.status === 'Inactive');

    // Global portfolio/platform dropdowns — applied client-side
    if (selectedPortfolio && selectedPortfolio !== 'all') data = data.filter(i => i.portfolio === selectedPortfolio);
    if (selectedPlatform && selectedPlatform !== 'all') data = data.filter(i => i.platform === selectedPlatform);

    if (decomPortfolioFilter.length) data = data.filter(i => decomPortfolioFilter.includes(i.portfolio));
    if (decomPlatformFilter.length) data = data.filter(i => decomPlatformFilter.includes(i.platform));
    if (decomStrategicIntentFilter.length) data = data.filter(i => decomStrategicIntentFilter.includes(i.strategic_intent?.intent || 'N/A'));
    if (decomStatusFilter.length) data = data.filter(i => decomStatusFilter.includes(i.status || 'N/A'));
    if (decomDecomStatusFilter.length) {
      data = data.filter(i => decomDecomStatusFilter.includes(calculateDecommissioningStatus(i)));
    }
    if (decomApplicationLabelsFilter.length) {
      data = data.filter(i => {
        if (Array.isArray(i.application_labels) && i.application_labels.length > 0) {
          return i.application_labels.some((l: any) => decomApplicationLabelsFilter.includes(l.name));
        }
        return decomApplicationLabelsFilter.includes('N/A');
      });
    }
    if (decomOwnersFilter) { const q = decomOwnersFilter.toLowerCase(); data = data.filter(i => (i.owner_names || '').toLowerCase().includes(q) || (i.owners || '').toLowerCase().includes(q)); }
    if (decomMvcGroupFilter.length) { data = data.filter(i => decomMvcGroupFilter.includes(i.mvc_group || 'N/A')); }
    if (decomDelayedCommentsFilter) { const q = decomDelayedCommentsFilter.toLowerCase(); data = data.filter(i => (i.decom_extension_reason || '').toLowerCase().includes(q)); }

    const applyDateFilter = (
      arr: DecommissioningItem[],
      getDate: (i: DecommissioningItem) => Date | null,
      filter: string,
      operator: string,
      customStart: string,
      customEnd: string
    ) => {
      if (!filter && !(operator === 'custom' && (customStart || customEnd))) return arr;
      return arr.filter(i => {
        const d = getDate(i);
        if (!d) return false;
        const itemDate = new Date(d); itemDate.setHours(0, 0, 0, 0);
        if (operator === 'custom') {
          const s = customStart ? new Date(customStart) : null;
          const e = customEnd ? new Date(customEnd) : null;
          if (s) s.setHours(0, 0, 0, 0);
          if (e) e.setHours(23, 59, 59, 999);
          if (s && e) return itemDate >= s && itemDate <= e;
          if (s) return itemDate >= s;
          if (e) return itemDate <= e;
          return true;
        }
        const fd = new Date(filter); fd.setHours(0, 0, 0, 0);
        if (operator === 'equals') return itemDate.getTime() === fd.getTime();
        if (operator === 'after') return itemDate > fd;
        if (operator === 'before') return itemDate < fd;
        return false;
      });
    };

    data = applyDateFilter(data, i => i.decom_date ? new Date(i.decom_date) : null, decomDecomDateFilter, decomDecomDateOperator, decomDecomDateCustomStart, decomDecomDateCustomEnd);
    data = applyDateFilter(data, i => i.failover_tested_date_last_12_months ? new Date(i.failover_tested_date_last_12_months) : null, decomFailoverTestedDateFilter, decomFailoverTestedDateOperator, decomFailoverTestedDateCustomStart, decomFailoverTestedDateCustomEnd);
    data = applyDateFilter(data, i => i.backup_tested_last_12_months ? new Date(i.backup_tested_last_12_months) : null, decomBackupTestedDateFilter, decomBackupTestedDateOperator, decomBackupTestedDateCustomStart, decomBackupTestedDateCustomEnd);
    if (decomPrevTargetDateOperator === 'allyears' && decomPrevTddSelectedYears.length > 0) {
      data = data.filter(i => {
        const allDates = getAllPrevTargetDates(i);
        return allDates.some(d => decomPrevTddSelectedYears.includes(d.getFullYear().toString()));
      });
    } else {
      data = applyDateFilter(data, i => getOldestPrevTargetDate(i), decomPrevTargetDateFilter, decomPrevTargetDateOperator, decomPrevTargetDateCustomStart, decomPrevTargetDateCustomEnd);
    }

    if (decomBiaStatusFilter.length) {
      data = data.filter(i => {
        const label = i.bia_status === true ? 'BIA Valid' : i.bia_status === false ? 'BIA Expired' : 'BIA - To be assessed';
        return decomBiaStatusFilter.includes(label);
      });
    }

    if (decomSuspendedFilter.length) {
      data = data.filter(i => {
        const label = i.suspended === true ? 'Yes' : i.suspended === false ? 'No' : 'N/A';
        return decomSuspendedFilter.includes(label);
      });
    }

    // Year filter
    if (decomYearFilter === 'all') {
      if (selectedYears.length) data = data.filter(i => i.target_date_decom && selectedYears.includes(new Date(i.target_date_decom).getFullYear().toString()));
    } else if (decomYearFilter === 'custom') {
      if (customRangeStart && customRangeEnd) {
        const s = new Date(customRangeStart), e = new Date(customRangeEnd);
        data = data.filter(i => { if (!i.target_date_decom) return false; const d = new Date(i.target_date_decom); return d >= s && d <= e; });
      }
    } else {
      data = data.filter(i => i.target_date_decom && new Date(i.target_date_decom).getFullYear().toString() === decomYearFilter);
    }

    // Tracking status chip filter
    if (decomTrackingFilter !== 'all') {
      if (decomTrackingFilter === 'completed') {
        data = data.filter(i => calculateDecommissioningStatus(i) === 'Decommissioned');
      } else if (decomTrackingFilter === 'ontrack') {
        data = data.filter(i => calculateDecommissioningStatus(i) === 'To be Decommissioned');
      } else if (decomTrackingFilter === 'delayed') {
        data = data.filter(i => calculateDecommissioningStatus(i) === 'Delayed');
      } else if (decomTrackingFilter === 'awaiting') {
        data = data.filter(i => calculateDecommissioningStatus(i) === 'Awaiting Decomm Confirmation');
      } else if (decomTrackingFilter === 'cancelled') {
        data = data.filter(i => calculateDecommissioningStatus(i) === 'Cancelled');
      }
    }

    // Text search
    if (decomSearchQuery) {
      const q = decomSearchQuery.toLowerCase();
      data = data.filter(i =>
        i.portfolio?.toLowerCase().includes(q) ||
        i.platform?.toLowerCase().includes(q) ||
        i.app_id?.toLowerCase().includes(q) ||
        i.app_name?.toLowerCase().includes(q)
      );
    }

    // Sort
    if (decomSortConfig) {
      data = [...data].sort((a: any, b: any) => {
        let av: any, bv: any;
        if (decomSortConfig.key === 'target_date_decom' || decomSortConfig.key === 'decom_date') {
          const da = a[decomSortConfig.key] ? new Date(a[decomSortConfig.key]) : null;
          const db = b[decomSortConfig.key] ? new Date(b[decomSortConfig.key]) : null;
          if (!da && !db) return 0; if (!da) return 1; if (!db) return -1;
          av = da.getTime(); bv = db.getTime();
        } else if (decomSortConfig.key === 'prev_target_date') {
          av = getOldestPrevTargetDate(a)?.getTime() || 0;
          bv = getOldestPrevTargetDate(b)?.getTime() || 0;
        } else if (decomSortConfig.key === 'decom_status') {
          av = calculateDecommissioningStatus(a); bv = calculateDecommissioningStatus(b);
        } else if (decomSortConfig.key === 'mvc_group') {
          av = a.mvc_group || '\uffff'; bv = b.mvc_group || '\uffff';
        } else if (decomSortConfig.key === 'p1p2') {
          const countsA = getIncidentCounts(a.app_id);
          const countsB = getIncidentCounts(b.app_id);
          av = countsA.p1 + countsA.p2;
          bv = countsB.p1 + countsB.p2;
        } else if (decomSortConfig.key === 'p3p4mi') {
          const countsA = getIncidentCounts(a.app_id);
          const countsB = getIncidentCounts(b.app_id);
          av = countsA.p3p4mi;
          bv = countsB.p3p4mi;
        } else if (decomSortConfig.key === 'cip_direct_cost') {
          const parse = (v: any) => { if (v == null) return -Infinity; const n = typeof v === 'string' ? parseFloat(v) : v; return isNaN(n) ? -Infinity : n; };
          av = parse(a.cip_direct_cost); bv = parse(b.cip_direct_cost);
        } else if (decomSortConfig.key === 'cip_indirect_cost') {
          const parse = (v: any) => { if (v == null) return -Infinity; const n = typeof v === 'string' ? parseFloat(v) : v; return isNaN(n) ? -Infinity : n; };
          av = parse(a.cip_indirect_cost); bv = parse(b.cip_indirect_cost);
        } else if (decomSortConfig.key === 'total_cost') {
          const parse = (v: any) => { if (v == null) return -Infinity; const n = typeof v === 'string' ? parseFloat(v) : v; return isNaN(n) ? -Infinity : n; };
          av = parse(a.total_cost); bv = parse(b.total_cost);
        } else if (decomSortConfig.key === 'bia_status') {
          const rank = (v: any) => v === true ? 1 : v === false ? 0 : -1;
          av = rank(a.bia_status); bv = rank(b.bia_status);
        } else if (decomSortConfig.key === 'cyber_risk') {
          const parse = (v: any) => { if (v == null) return -Infinity; const n = typeof v === 'string' ? parseFloat(v) : v; return isNaN(n) ? -Infinity : n; };
          av = parse(a.risk_exposure_all); bv = parse(b.risk_exposure_all);
        } else if (decomSortConfig.key === 'suspended') {
          const rank = (v: any) => v === false ? 2 : v === true ? 1 : 0;
          av = rank(a.suspended); bv = rank(b.suspended);
        } else if (decomSortConfig.key === 'backup_tested_last_12_months') {
          const da = a.backup_tested_last_12_months ? new Date(a.backup_tested_last_12_months) : null;
          const db = b.backup_tested_last_12_months ? new Date(b.backup_tested_last_12_months) : null;
          if (!da && !db) return 0; if (!da) return 1; if (!db) return -1;
          av = da.getTime(); bv = db.getTime();
        } else if (decomSortConfig.key === 'failover_tested_date_last_12_months') {
          const da = a.failover_tested_date_last_12_months ? new Date(a.failover_tested_date_last_12_months) : null;
          const db = b.failover_tested_date_last_12_months ? new Date(b.failover_tested_date_last_12_months) : null;
          if (!da && !db) return 0; if (!da) return 1; if (!db) return -1;
          av = da.getTime(); bv = db.getTime();
        } else {
          av = a[decomSortConfig.key] || ''; bv = b[decomSortConfig.key] || '';
        }
        if (av < bv) return decomSortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return decomSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [
    enrichedDecommissioningData, selectedPortfolio, selectedPlatform, decomPortfolioFilter, decomPlatformFilter, decomStrategicIntentFilter,
    decomStatusFilter, decomDecomStatusFilter, decomApplicationLabelsFilter, decomOwnersFilter,
    decomDelayedCommentsFilter, decomDecomDateFilter, decomDecomDateOperator, decomDecomDateCustomStart,
    decomDecomDateCustomEnd, decomFailoverTestedDateFilter, decomFailoverTestedDateOperator,
    decomFailoverTestedDateCustomStart, decomFailoverTestedDateCustomEnd, decomBackupTestedDateFilter,
    decomBackupTestedDateOperator, decomBackupTestedDateCustomStart, decomBackupTestedDateCustomEnd,
    decomPrevTargetDateFilter, decomPrevTargetDateOperator, decomPrevTargetDateCustomStart,
    decomPrevTargetDateCustomEnd, decomBiaStatusFilter, decomSuspendedFilter, decomYearFilter, selectedYears,
    decomPrevTddSelectedYears,
    customRangeStart, customRangeEnd, decomTrackingFilter, decomSearchQuery, decomSortConfig,
    calculateDecommissioningStatus, getOldestPrevTargetDate,
    decomMvcGroupFilter, getIncidentCounts,
  ]);

  // ─── CSV Export ───────────────────────────────────────────────────────────

  const handleExportCSV = () => {
    try {
      const filteredData = buildFilteredData();
      const cv = decomColumnVisibility;
      const headers: string[] = [];
      if (cv.portfolio) headers.push('Portfolio');
      if (cv.platform) headers.push('Platform');
      if (cv.app_id) headers.push('App ID');
      if (cv.app_name) headers.push('App Name');
      if (cv.mvc_group) headers.push('MVC Group');
      if (cv.owners) { headers.push('Owner Name'); headers.push('Owner Email'); }
      if (cv.target_date) headers.push('Target Decom Date');
      if (cv.prev_target_date) headers.push('Prev Target Date');
      if (cv.decom_date) headers.push('Decom Date');
      if (cv.delayed_comments) headers.push('Delayed Comments from Mac');
      if (cv.strategic_intent) headers.push('Strategic Intent');
      if (cv.backup_tested) headers.push('Restore from Backup Tested (Last 12 Months)');
      if (cv.failover_tested) headers.push('Failover Tested Date (Last 12 Months)');
      if (cv.status) headers.push('MAC Status');
      if (cv.decom_status) { headers.push('Decom Status'); headers.push('Change Description'); }
      if (cv.application_labels) headers.push('Application Labels');
      if (cv.cip_direct_cost) headers.push('CIP Direct Cost');
      if (cv.cip_indirect_cost) headers.push('CIP Indirect Cost');
      if (cv.total_cost) headers.push('Total Approx Cost (Last 12 months)');
      if (cv.bia_status) headers.push('BIA Status');
      if (cv.cyber_risk) headers.push('Cyber Risk Exposure');
      if (cv.suspended) headers.push('Suspended');

      const rows = [headers.join(',')];
      filteredData.forEach(item => {
        const status = calculateDecommissioningStatus(item);
        const row: string[] = [];
        if (cv.portfolio) row.push(item.portfolio || 'N/A');
        if (cv.platform) row.push(item.platform || 'N/A');
        if (cv.app_id) row.push(item.app_id || 'N/A');
        if (cv.app_name) row.push(item.app_name || 'N/A');
        if (cv.mvc_group) row.push(item.mvc_group || 'N/A');
        if (cv.owners) { row.push(item.owner_names || 'N/A'); row.push(item.owners || 'N/A'); }
        if (cv.target_date) row.push(item.target_date_decom ? new Date(item.target_date_decom).toLocaleDateString() : 'N/A');
        if (cv.prev_target_date) {
          const allDates = getAllPrevTargetDates(item);
          row.push(allDates.length > 0 ? allDates.map(d => d.toLocaleDateString()).join('; ') : 'N/A');
        }
        if (cv.decom_date) row.push(item.decom_date ? new Date(item.decom_date).toLocaleDateString() : 'N/A');
        if (cv.delayed_comments) row.push(item.decom_extension_reason || 'N/A');
        if (cv.strategic_intent) row.push(item.strategic_intent?.intent || 'N/A');
        if (cv.backup_tested) row.push(item.backup_tested_last_12_months === 'Y' ? 'Yes' : item.backup_tested_last_12_months === 'N' ? 'No' : 'N/A');
        if (cv.failover_tested) row.push(item.failover_tested_date_last_12_months ? new Date(item.failover_tested_date_last_12_months).toLocaleDateString() : 'N/A');
        if (cv.status) row.push(item.status || 'N/A');
        if (cv.decom_status) { row.push(status); row.push(item.decom_change_description || 'N/A'); }
        if (cv.application_labels) row.push(Array.isArray(item.application_labels) && item.application_labels.length ? item.application_labels.map((l: any) => l.name).join(', ') : 'N/A');
        if (cv.cip_direct_cost) {
          if (item.cip_direct_cost != null) {
            const n = typeof item.cip_direct_cost === 'string' ? parseFloat(item.cip_direct_cost as any) : item.cip_direct_cost;
            const amt = !isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not Available';
            row.push(amt);
          } else { row.push('Not Available'); }
        }
        if (cv.cip_indirect_cost) {
          if (item.cip_indirect_cost != null) {
            const n = typeof item.cip_indirect_cost === 'string' ? parseFloat(item.cip_indirect_cost as any) : item.cip_indirect_cost;
            const amt = !isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not Available';
            row.push(amt);
          } else { row.push('Not Available'); }
        }
        if (cv.total_cost) {
          if (item.total_cost != null) {
            const n = typeof item.total_cost === 'string' ? parseFloat(item.total_cost as any) : item.total_cost;
            row.push(!isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not Available');
          } else { row.push('Not Available'); }
        }
        if (cv.bia_status) row.push(item.bia_status === true ? 'BIA Valid' : item.bia_status === false ? 'BIA Expired' : 'BIA - To be assessed');
        if (cv.cyber_risk) row.push(item.risk_exposure_all != null ? String(item.risk_exposure_all) : 'N/A');
        if (cv.suspended) row.push(item.suspended === true ? 'Yes' : item.suspended === false ? 'No' : '-');
        rows.push(row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','));
      });

      const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `decommissioning_status_${new Date().toISOString().split('T')[0]}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (e) { console.error('Export error', e); }
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────

  const getTrackingStatusStyle = (status: string) => {
    switch (status) {
      case 'Decommissioned': return { label: 'Decommissioned', bgcolor: '#2e7d32', color: '#fff' };
      case 'To be Decommissioned': return { label: 'To be Decommissioned', bgcolor: '#1976d2', color: '#fff' };
      case 'Delayed': return { label: 'Delayed', bgcolor: '#c62828', color: '#fff' };
      case 'Awaiting Decomm Confirmation': return { label: 'Awaiting Decomm Confirmation', bgcolor: '#e65100', color: '#fff' };
      case 'Cancelled': return { label: 'Cancelled', bgcolor: '#666666', color: '#fff' };
      default: return { label: status || 'Unknown', bgcolor: '#9e9e9e', color: '#fff' };
    }
  };

  const getStatusTooltip = (status: string) => {
    switch (status) {
      case 'Decommissioned': return 'Application is fully turned off';
      case 'To be Decommissioned': return 'Planned, on track, no delay yet';
      case 'Delayed': return 'Decommission timeline extended beyond the original target date; application remains active.';
      case 'Awaiting Decomm Confirmation': return 'Target date has passed; owner action required to confirm decommission or update timeline in MAC.';
      case 'Cancelled': return 'No longer planned for decommission';
      default: return 'Unknown status';
    }
  };

  // Status counts for chips — applies all active filters except the tracking status chip itself
  const validDataForCounts = useMemo(() => {
    let data = enrichedDecommissioningData.filter(i => i.target_date_decom);
    data = data.filter(i => i.status === 'Active' || i.status === 'Inactive');

    // Global portfolio/platform dropdowns
    if (selectedPortfolio && selectedPortfolio !== 'all') data = data.filter(i => i.portfolio === selectedPortfolio);
    if (selectedPlatform && selectedPlatform !== 'all') data = data.filter(i => i.platform === selectedPlatform);

    // Column-level filters
    if (decomPortfolioFilter.length) data = data.filter(i => decomPortfolioFilter.includes(i.portfolio));
    if (decomPlatformFilter.length) data = data.filter(i => decomPlatformFilter.includes(i.platform));
    if (decomStrategicIntentFilter.length) data = data.filter(i => decomStrategicIntentFilter.includes(i.strategic_intent?.intent || 'N/A'));
    if (decomStatusFilter.length) data = data.filter(i => decomStatusFilter.includes(i.status || 'N/A'));
    if (decomDecomStatusFilter.length) data = data.filter(i => decomDecomStatusFilter.includes(calculateDecommissioningStatus(i)));
    if (decomApplicationLabelsFilter.length) {
      data = data.filter(i => {
        if (Array.isArray(i.application_labels) && i.application_labels.length > 0) {
          return i.application_labels.some((l: any) => decomApplicationLabelsFilter.includes(l.name));
        }
        return decomApplicationLabelsFilter.includes('N/A');
      });
    }
    if (decomOwnersFilter) { const q = decomOwnersFilter.toLowerCase(); data = data.filter(i => (i.owner_names || '').toLowerCase().includes(q) || (i.owners || '').toLowerCase().includes(q)); }
    if (decomDelayedCommentsFilter) { const q = decomDelayedCommentsFilter.toLowerCase(); data = data.filter(i => (i.decom_extension_reason || '').toLowerCase().includes(q)); }

    const applyDateFilterForCounts = (
      arr: DecommissioningItem[],
      getDate: (i: DecommissioningItem) => Date | null,
      filter: string,
      operator: string,
      customStart: string,
      customEnd: string
    ) => {
      if (!filter && !(operator === 'custom' && (customStart || customEnd))) return arr;
      return arr.filter(i => {
        const d = getDate(i);
        if (!d) return false;
        const itemDate = new Date(d); itemDate.setHours(0, 0, 0, 0);
        if (operator === 'custom') {
          const s = customStart ? new Date(customStart) : null;
          const e = customEnd ? new Date(customEnd) : null;
          if (s) s.setHours(0, 0, 0, 0);
          if (e) e.setHours(23, 59, 59, 999);
          if (s && e) return itemDate >= s && itemDate <= e;
          if (s) return itemDate >= s;
          if (e) return itemDate <= e;
          return true;
        }
        const fd = new Date(filter); fd.setHours(0, 0, 0, 0);
        if (operator === 'equals') return itemDate.getTime() === fd.getTime();
        if (operator === 'after') return itemDate > fd;
        if (operator === 'before') return itemDate < fd;
        return false;
      });
    };

    data = applyDateFilterForCounts(data, i => i.decom_date ? new Date(i.decom_date) : null, decomDecomDateFilter, decomDecomDateOperator, decomDecomDateCustomStart, decomDecomDateCustomEnd);
    data = applyDateFilterForCounts(data, i => i.failover_tested_date_last_12_months ? new Date(i.failover_tested_date_last_12_months) : null, decomFailoverTestedDateFilter, decomFailoverTestedDateOperator, decomFailoverTestedDateCustomStart, decomFailoverTestedDateCustomEnd);
    data = applyDateFilterForCounts(data, i => i.backup_tested_last_12_months ? new Date(i.backup_tested_last_12_months) : null, decomBackupTestedDateFilter, decomBackupTestedDateOperator, decomBackupTestedDateCustomStart, decomBackupTestedDateCustomEnd);
    data = applyDateFilterForCounts(data, i => getOldestPrevTargetDate(i), decomPrevTargetDateFilter, decomPrevTargetDateOperator, decomPrevTargetDateCustomStart, decomPrevTargetDateCustomEnd);

    if (decomBiaStatusFilter.length) {
      data = data.filter(i => {
        const label = i.bia_status === true ? 'BIA Valid' : i.bia_status === false ? 'BIA Expired' : 'BIA - To be assessed';
        return decomBiaStatusFilter.includes(label);
      });
    }

    if (decomSuspendedFilter.length) {
      data = data.filter(i => {
        const label = i.suspended === true ? 'Yes' : i.suspended === false ? 'No' : 'N/A';
        return decomSuspendedFilter.includes(label);
      });
    }

    // Year filter
    if (decomYearFilter === 'all') {
      if (selectedYears.length) data = data.filter(i => i.target_date_decom && selectedYears.includes(new Date(i.target_date_decom).getFullYear().toString()));
    } else if (decomYearFilter === 'custom') {
      if (customRangeStart && customRangeEnd) { const s = new Date(customRangeStart), e = new Date(customRangeEnd); data = data.filter(i => { if (!i.target_date_decom) return false; const d = new Date(i.target_date_decom); return d >= s && d <= e; }); }
    } else {
      data = data.filter(i => i.target_date_decom && new Date(i.target_date_decom).getFullYear().toString() === decomYearFilter);
    }

    // Text search (also affects counts)
    if (decomSearchQuery) {
      const q = decomSearchQuery.toLowerCase();
      data = data.filter(i =>
        i.portfolio?.toLowerCase().includes(q) ||
        i.platform?.toLowerCase().includes(q) ||
        i.app_id?.toLowerCase().includes(q) ||
        i.app_name?.toLowerCase().includes(q)
      );
    }

    return data;
  }, [
    enrichedDecommissioningData, selectedPortfolio, selectedPlatform, decomPortfolioFilter, decomPlatformFilter,
    decomStrategicIntentFilter, decomStatusFilter, decomDecomStatusFilter, decomApplicationLabelsFilter,
    decomOwnersFilter, decomDelayedCommentsFilter, decomDecomDateFilter, decomDecomDateOperator,
    decomDecomDateCustomStart, decomDecomDateCustomEnd, decomFailoverTestedDateFilter,
    decomFailoverTestedDateOperator, decomFailoverTestedDateCustomStart, decomFailoverTestedDateCustomEnd,
    decomBackupTestedDateFilter, decomBackupTestedDateOperator, decomBackupTestedDateCustomStart,
    decomBackupTestedDateCustomEnd, decomPrevTargetDateFilter, decomPrevTargetDateOperator,
    decomPrevTargetDateCustomStart, decomPrevTargetDateCustomEnd, decomBiaStatusFilter, decomSuspendedFilter,
    decomYearFilter, selectedYears, customRangeStart, customRangeEnd, decomSearchQuery,
    calculateDecommissioningStatus, getOldestPrevTargetDate,
  ]);

  const statusCounts = useMemo(() => ({
    all: validDataForCounts.length,
    decommissioned: validDataForCounts.filter(i => calculateDecommissioningStatus(i) === 'Decommissioned').length,
    toBeDecommissioned: validDataForCounts.filter(i => calculateDecommissioningStatus(i) === 'To be Decommissioned').length,
    delayed: validDataForCounts.filter(i => calculateDecommissioningStatus(i) === 'Delayed').length,
    awaiting: validDataForCounts.filter(i => calculateDecommissioningStatus(i) === 'Awaiting Decomm Confirmation').length,
    cancelled: validDataForCounts.filter(i => calculateDecommissioningStatus(i) === 'Cancelled').length,
  }), [validDataForCounts, calculateDecommissioningStatus]);

  const sortLabel = (key: string, label: string) => (
    <TableSortLabel
      active={decomSortConfig?.key === key}
      direction={decomSortConfig?.key === key ? decomSortConfig.direction : 'asc'}
      onClick={() => {
        const isAsc = decomSortConfig?.key === key && decomSortConfig.direction === 'asc';
        setDecomSortConfig({ key, direction: isAsc ? 'desc' : 'asc' });
      }}
    >
      <strong>{label}</strong>
    </TableSortLabel>
  );

  const filterIconBtn = (active: boolean, onClick: (e: React.MouseEvent<HTMLElement>) => void) => (
    <IconButton size="small" onClick={onClick} sx={{ padding: '2px', color: active ? '#1976d2' : '#666' }}>
      {active ? <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} /> : <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />}
    </IconButton>
  );

  // CheckboxListPopover and DateFilterPopover are defined at module level (outside this component) to prevent remount blinking

  const filteredData = buildFilteredData();
  const paginatedData = filteredData.slice(decomPage * decomRowsPerPage, decomPage * decomRowsPerPage + decomRowsPerPage);

  // For Trends: apply only portfolio/platform global filters (not year chip / tracking status chip)
  const filteredDataForTrends = useMemo(() => {
    let data = enrichedDecommissioningData.filter(i => i.status === 'Active' || i.status === 'Inactive');
    if (selectedPortfolio && selectedPortfolio !== 'all') data = data.filter(i => i.portfolio === selectedPortfolio);
    if (selectedPlatform && selectedPlatform !== 'all') data = data.filter(i => i.platform === selectedPlatform);
    return data;
  }, [enrichedDecommissioningData, selectedPortfolio, selectedPlatform]);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (decommissioningLoading && decommissioningData.length === 0) {
    return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#0067b3' }} />
          <Typography variant="h6" color="textSecondary">
            Loading Decommissioning Status Data...
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Fetching application decommissioning records
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 2, pt: 2, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Alert
        severity="warning"
        icon={<Warning sx={{ fontSize: 30 }} />}
        sx={{
          mb: 2,
          border: '1px solid #f59e0b',
          borderLeft: '6px solid #d97706',
          borderRadius: 1,
          backgroundColor: '#fff8e1',
          boxShadow: '0 2px 8px rgba(180, 83, 9, 0.12)',
          '& .MuiAlert-icon': { color: '#b45309', alignItems: 'flex-start', mt: 0.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 700, color: '#7c2d12', mb: 0.5 }}>
              Dashboard Migration Notice
            </Typography>
            <Typography variant="body1" sx={{ color: '#7c2d12', lineHeight: 1.6 }}>
              As part of our transition to MTS, this dashboard will be decommissioned from Operational Excellence on <strong>15th October</strong>. This link will redirect automatically to MTS after that date.
            </Typography>
            <Typography variant="body2" sx={{ color: '#7c2d12', mt: 0.5 }}>
              Need help? Visit the <strong>MTS Support Channel</strong>.
            </Typography>
          </Box>
          <Button component="a" href="https://mts.maersk.io/bowler/dr/decommission?view=bowler" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ flexShrink: 0, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
            Maersk | MTS
          </Button>
        </Box>
      </Alert>
      {/* Tag Modal */}
      <Dialog open={tagModalOpen} onClose={handleCloseTagModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ fontWeight: 600 }}>{isEditMode ? 'Edit Tag' : 'Add Tag'} — {selectedTagApp?.app_name}</Box>
          <IconButton onClick={handleCloseTagModal} size="small" aria-label="close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Decommissioning Tag" value={tagFormData.decomm_tag}
                  onChange={e => setTagFormData(p => ({ ...p, decomm_tag: e.target.value }))}
                  placeholder="e.g. OP26, OP27" helperText="Enter a tag to categorize this application" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Remarks" value={tagFormData.remarks}
                  onChange={e => setTagFormData(p => ({ ...p, remarks: e.target.value }))}
                  placeholder="Enter remarks or notes" helperText="Remarks will appear as a tooltip when hovering over the tag" multiline rows={2} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          {isEditMode && (
            <Button onClick={handleDeleteTag} disabled={tagLoading || tagDeleteLoading} startIcon={<DeleteOutline />} sx={{ color: '#d32f2f', textTransform: 'none' }}>
              {tagDeleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <Button onClick={handleSaveTag} variant="contained" disabled={tagLoading || tagDeleteLoading || !tagFormData.decomm_tag || (isEditMode && tagFormData.decomm_tag === (selectedTagApp?.tag?.decomm_tag || '') && tagFormData.remarks === (selectedTagApp?.tag?.comments || ''))}>
            {tagLoading ? (isEditMode ? 'Updating...' : 'Saving...') : isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters Panel */}
      <Paper sx={{ mx: 0, mt: 0, mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e1e5e9', borderRadius: '8px' }}>
        {/* Title + data source row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAlt sx={{ color: '#2c3e50', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50' }}>Filters</Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '11px', mt: 0.5 }}>
              Application Data Source:&nbsp;
              <Link href="https://mac.maersk.io/" target="_blank" rel="noopener noreferrer" sx={{ color: '#0067b3', textDecoration: 'underline' }}>MAC</Link>
              &nbsp;| Refresh Frequency – Daily 23:00 UTC
              &nbsp;| Cost Data Source Details:&nbsp;
              <Link href="https://app.powerbi.com/groups/me/apps/1161082c-c885-4a6f-b5df-043860c73439/reports/786adebb-a253-4ebd-a30c-d29fd8ad4070/ReportSection94ba8c76798120510b97?ctid=05d75c05-fa1a-42e7-9cf1-eb416c396f2d&experience=power-bi" target="_blank" rel="noopener noreferrer" sx={{ color: '#0067b3', textDecoration: 'underline' }}>ITFM DCO</Link>
              &nbsp;(Total Approx Cost — Manual Refresh)
              &nbsp;| Cyber Risk Data:&nbsp;<Link href="https://maersk.archerirm.eu/ngrx-ui/index.html#/search-results?reportId=13096&moduleId=70" target="_blank" rel="noopener noreferrer" sx={{ color: '#0067b3', textDecoration: 'underline' }}>Cyber Risk Exposure</Link><span style={{ color: '#333' }}>&nbsp;(Manual Refresh of data)</span>
              &nbsp;| <Link href="https://maersk-tools.atlassian.net/wiki/spaces/TOP/pages/183775561107/Application+Decommission" target="_blank" rel="noopener noreferrer" sx={{ color: '#0067b3', textDecoration: 'underline' }}>App Decommission Process</Link>
            </Typography>
          </Box>
        </Box>

        {/* Filter controls */}
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>Platform Portfolio</InputLabel>
              <Select
                value={selectedPortfolio}
                label="Platform Portfolio"
                onChange={e => { setSelectedPortfolio(e.target.value); setDecomPage(0); }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                {portfolioOptions.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '13px' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>Platform</InputLabel>
              <Select
                value={selectedPlatform}
                label="Platform"
                onChange={e => { setSelectedPlatform(e.target.value); setDecomPage(0); }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                {platformOptions.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '13px' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Portfolio, Platform, App ID, App Name..."
              value={decomSearchQuery}
              onChange={e => { setDecomSearchQuery(e.target.value); setDecomPage(0); }}
              sx={{ '& .MuiInputBase-root': { backgroundColor: '#ffffff', fontSize: '13px' } }}
              InputProps={{ startAdornment: <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}><Search sx={{ fontSize: 18, color: '#aaa' }} /></Box> }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table / Trends / Bowler toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#e4e8ec', width: '100%', mb: 2, px: 0.5, py: '3px', borderRadius: '6px' }}>
        {(['table', 'trends', 'bowler'] as const).map(v => (
          <Button key={v} size="small"
            onClick={() => handleDecomViewChange(v)}
            sx={{
              bgcolor: decomView === v ? '#1e3a5f' : 'transparent',
              color: decomView === v ? '#ffffff' : '#555',
              textTransform: 'none',
              fontWeight: decomView === v ? 700 : 400,
              fontSize: '13px',
              borderRadius: '4px',
              px: 2,
              py: 0.5,
              minWidth: 0,
              boxShadow: decomView === v ? '0 1px 4px rgba(0,0,0,0.25)' : 'none',
              '&:hover': { bgcolor: decomView === v ? '#16304f' : 'rgba(0,0,0,0.06)' },
            }}>
            {v === 'table' ? 'Table View' : v === 'trends' ? 'Trends' : 'ERM Bowler Dashboard'}
          </Button>
        ))}
      </Box>

      {decomView === 'trends' ? (
        <DecommissionTrends decommissioningData={filteredDataForTrends} />
      ) : decomView === 'bowler' ? (
        <DecomBowlerMetrics decommissioningData={filteredDataForTrends} />
      ) : decommissioningLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : decommissioningError ? (
        <Alert severity="error">{decommissioningError}</Alert>
      ) : (
        <Box>
          {/* Status chips + actions — single row, horizontally scrollable */}
          <Box sx={{ mb: 2, overflowX: 'auto', overflowY: 'visible', pb: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'nowrap', minWidth: 'max-content' }}>
              {([
                { key: 'all', label: `All (${statusCounts.all})`, bg: '#1976d2', color: '#fff' },
                { key: 'completed', label: `Decommissioned (${statusCounts.decommissioned})`, bg: '#2e7d32', color: '#fff' },
                { key: 'ontrack', label: `To be Decommissioned (${statusCounts.toBeDecommissioned})`, bg: '#1976d2', color: '#fff' },
                { key: 'delayed', label: `Delayed (${statusCounts.delayed})`, bg: '#c62828', color: '#fff' },
                { key: 'awaiting', label: `Awaiting Decomm Confirmation (${statusCounts.awaiting})`, bg: '#e65100', color: '#fff' },
                { key: 'cancelled', label: `Cancelled (${statusCounts.cancelled})`, bg: '#666666', color: '#fff' },
              ] as Array<{ key: typeof decomTrackingFilter; label: string; bg: string; color: string }>).map(({ key, label, bg, color }) => (
                <Chip key={key} label={label}
                  onClick={() => { setDecomTrackingFilter(key); setDecomPage(0); }}
                  variant={decomTrackingFilter === key ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.77rem', height: '30px', bgcolor: decomTrackingFilter === key ? `${bg} !important` : 'transparent', color: decomTrackingFilter === key ? `${color} !important` : bg, borderColor: bg, '&:hover': { bgcolor: `${bg}22 !important` } }} />
              ))}
              <IconButton onClick={() => setDecomColumnDialogOpen(true)} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' }, border: '1px solid #ccc', borderRadius: 1, width: '36px', height: '36px', ml: 0.5 }} title="Column Settings">
                <Settings sx={{ color: '#666', fontSize: '17px' }} />
              </IconButton>
              {canManageDcoCsv && (
                <MuiTooltip title={dcoCsvLoading ? 'Click to cancel DCO CSV load' : 'Load DCO cost analysis CSV'} arrow>
                  <Button variant="contained" color={dcoCsvLoading ? 'success' : 'primary'} component="label"
                    onClick={async e => {
                      if (dcoCsvLoading) {
                        e.preventDefault(); e.stopPropagation();
                        if (dcoCsvAbortControllerRef.current && window.confirm('Cancel the current DCO CSV load?')) {
                          dcoCsvAbortControllerRef.current.abort();
                          try { await authFetch(DCO.CANCEL_LOAD, { method: 'POST' }); } catch { }
                        }
                      }
                    }}
                    sx={{ textTransform: 'none', height: '36px', px: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {dcoCsvLoading ? 'Loading DCO...' : 'Load DCO'}
                    {!dcoCsvLoading && (
                      <input type="file" accept=".csv,text/csv" hidden onChange={async event => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        setDcoCsvLoading(true);
                        try {
                          const controller = new AbortController();
                          dcoCsvAbortControllerRef.current = controller;
                          const formData = new FormData();
                          formData.append('file', file);
                          const resp = await authFetch(DCO.LOAD_CSV, { method: 'POST', body: formData, signal: controller.signal });
                          if (!resp.ok) throw new Error(`Failed to load DCO CSV (${resp.status})`);
                          setTagSuccessMessage('DCO cost analysis CSV loaded successfully');
                          await fetchDecommissioningData();
                        } catch (err: any) {
                          if (err?.name !== 'AbortError') alert(err?.message || 'Failed to load DCO CSV');
                        } finally {
                          setDcoCsvLoading(false);
                          dcoCsvAbortControllerRef.current = null;
                          event.target.value = '';
                        }
                      }} />
                    )}
                  </Button>
                </MuiTooltip>
              )}
              <Button variant="contained" onClick={handleExportCSV}
                sx={{ textTransform: 'none', bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, height: '36px', px: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Table */}
          {decommissioningData.length === 0 ? (
            <Alert severity="info">No decommissioning data available for the selected filters.</Alert>
          ) : (
            <Paper sx={{ boxShadow: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      {decomColumnVisibility.portfolio && (
                        <TableCell><strong>Portfolio</strong></TableCell>
                      )}
                      {decomColumnVisibility.platform && (
                        <TableCell><strong>Platform</strong></TableCell>
                      )}
                      {decomColumnVisibility.app_id && <TableCell><strong>App ID</strong></TableCell>}
                      {decomColumnVisibility.app_name && <TableCell><strong>App Name</strong></TableCell>}
                      {decomColumnVisibility.mvc_group && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('mvc_group', 'MVC Group')}
                            {filterIconBtn(decomMvcGroupFilter.length > 0, e => { setDecomMvcGroupFilterAnchor(e.currentTarget); setDecomMvcGroupTempFilter([...decomMvcGroupFilter]); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.owners && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>Owners</strong>
                            {filterIconBtn(!!decomOwnersFilter, e => { setDecomOwnersFilterAnchor(e.currentTarget); setDecomOwnersTempFilter(decomOwnersFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.target_date && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('target_date_decom', 'Target Decom Date')}
                            {filterIconBtn(decomYearFilter !== 'all', e => setDecomYearFilterAnchor(e.currentTarget))}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.prev_target_date && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('prev_target_date', 'Prev Target Date')}
                            {filterIconBtn(!!decomPrevTargetDateFilter || decomPrevTddSelectedYears.length > 0, e => { setDecomPrevTargetDateFilterAnchor(e.currentTarget); setDecomPrevTargetDateTempFilter(decomPrevTargetDateFilter); setDecomPrevTargetDateTempOperator(decomPrevTargetDateOperator); setDecomPrevTargetDateTempCustomStart(decomPrevTargetDateCustomStart); setDecomPrevTargetDateTempCustomEnd(decomPrevTargetDateCustomEnd); setDecomPrevTddTempSelectedYears(decomPrevTddSelectedYears); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.decom_date && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('decom_date', 'Decom Date')}
                            {filterIconBtn(!!decomDecomDateFilter, e => { setDecomDecomDateFilterAnchor(e.currentTarget); setDecomDecomDateTempFilter(decomDecomDateFilter); setDecomDecomDateTempOperator(decomDecomDateOperator); setDecomDecomDateTempCustomStart(decomDecomDateCustomStart); setDecomDecomDateTempCustomEnd(decomDecomDateCustomEnd); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.delayed_comments && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>Delayed Comments from Mac</strong>
                            {filterIconBtn(!!decomDelayedCommentsFilter, e => { setDecomDelayedCommentsFilterAnchor(e.currentTarget); setDecomDelayedCommentsTempFilter(decomDelayedCommentsFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.strategic_intent && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>Strategic Intent</strong>
                            {filterIconBtn(decomStrategicIntentFilter.length > 0, e => { setDecomStrategicIntentFilterAnchor(e.currentTarget); setDecomStrategicIntentTempFilter(decomStrategicIntentFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.backup_tested && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('backup_tested_last_12_months', 'Restore from Backup Tested (Last 12 Mo)')}
                            {filterIconBtn(!!decomBackupTestedDateFilter, e => { setDecomBackupTestedDateFilterAnchor(e.currentTarget); setDecomBackupTestedDateTempFilter(decomBackupTestedDateFilter); setDecomBackupTestedDateTempOperator(decomBackupTestedDateOperator); setDecomBackupTestedDateTempCustomStart(decomBackupTestedDateCustomStart); setDecomBackupTestedDateTempCustomEnd(decomBackupTestedDateCustomEnd); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.failover_tested && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('failover_tested_date_last_12_months', 'Failover Tested Date (Last 12 Mo)')}
                            {filterIconBtn(!!decomFailoverTestedDateFilter, e => { setDecomFailoverTestedDateFilterAnchor(e.currentTarget); setDecomFailoverTestedDateTempFilter(decomFailoverTestedDateFilter); setDecomFailoverTestedDateTempOperator(decomFailoverTestedDateOperator); setDecomFailoverTestedDateTempCustomStart(decomFailoverTestedDateCustomStart); setDecomFailoverTestedDateTempCustomEnd(decomFailoverTestedDateCustomEnd); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.status && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>MAC Status</strong>
                            {filterIconBtn(decomStatusFilter.length > 0, e => { setDecomStatusFilterAnchor(e.currentTarget); setDecomStatusTempFilter(decomStatusFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.decom_status && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('decom_status', 'Decom Status')}
                            {filterIconBtn(decomDecomStatusFilter.length > 0, e => { setDecomDecomStatusFilterAnchor(e.currentTarget); setDecomDecomStatusTempFilter(decomDecomStatusFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.application_labels && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>Application Labels</strong>
                            {filterIconBtn(decomApplicationLabelsFilter.length > 0, e => { setDecomApplicationLabelsFilterAnchor(e.currentTarget); setDecomApplicationLabelsTempFilter(decomApplicationLabelsFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.p1p2 && (
                        <TableCell>
                          <MuiTooltip title="Incident count period: year to date" arrow placement="top">
                            <TableSortLabel
                              active={decomSortConfig?.key === 'p1p2'}
                              direction={decomSortConfig?.key === 'p1p2' ? decomSortConfig.direction : 'asc'}
                              onClick={() => {
                                const isAsc = decomSortConfig?.key === 'p1p2' && decomSortConfig.direction === 'asc';
                                setDecomSortConfig({ key: 'p1p2', direction: isAsc ? 'desc' : 'asc' });
                              }}
                            >
                              <strong>P1/P2 Incidents</strong>
                            </TableSortLabel>
                          </MuiTooltip>
                        </TableCell>
                      )}
                      {decomColumnVisibility.p3p4mi && (
                        <TableCell>
                          <MuiTooltip title="Incident count period: year to date" arrow placement="top">
                            <TableSortLabel
                              active={decomSortConfig?.key === 'p3p4mi'}
                              direction={decomSortConfig?.key === 'p3p4mi' ? decomSortConfig.direction : 'asc'}
                              onClick={() => {
                                const isAsc = decomSortConfig?.key === 'p3p4mi' && decomSortConfig.direction === 'asc';
                                setDecomSortConfig({ key: 'p3p4mi', direction: isAsc ? 'desc' : 'asc' });
                              }}
                            >
                              <strong>P3/P4 Incidents</strong>
                            </TableSortLabel>
                          </MuiTooltip>
                        </TableCell>
                      )}
                      {decomColumnVisibility.tag && <TableCell>{sortLabel('tag', 'Tag')}</TableCell>}
                      {decomColumnVisibility.cip_direct_cost && <TableCell>{sortLabel('cip_direct_cost', 'CIP Direct Cost')}</TableCell>}
                      {decomColumnVisibility.cip_indirect_cost && <TableCell>{sortLabel('cip_indirect_cost', 'CIP Indirect Cost')}</TableCell>}
                      {decomColumnVisibility.total_cost && <TableCell>{sortLabel('total_cost', 'Total Approx Cost (Last 12 mo)')}</TableCell>}
                      {decomColumnVisibility.bia_status && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sortLabel('bia_status', 'BIA Status')}
                            {filterIconBtn(decomBiaStatusFilter.length > 0, e => { setDecomBiaStatusFilterAnchor(e.currentTarget); setDecomBiaStatusTempFilter(decomBiaStatusFilter); })}
                          </Box>
                        </TableCell>
                      )}
                      {decomColumnVisibility.cyber_risk && (
                        <TableCell>
                          <MuiTooltip title="Current open cyber risk exposure (Risk - Exposure All)" arrow placement="top">
                            <span>{sortLabel('cyber_risk', 'Cyber Risk Exposure')}</span>
                          </MuiTooltip>
                        </TableCell>
                      )}
                      {decomColumnVisibility.suspended && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MuiTooltip title="MAC suspended flag — Yes means the app was system-decommissioned by MAC" arrow placement="top">
                              <span>{sortLabel('suspended', 'Suspended')}</span>
                            </MuiTooltip>
                            {filterIconBtn(decomSuspendedFilter.length > 0, e => { setDecomSuspendedFilterAnchor(e.currentTarget); setDecomSuspendedTempFilter(decomSuspendedFilter); })}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((item, idx) => {
                      const status = calculateDecommissioningStatus(item);
                      const style = getTrackingStatusStyle(status);
                      const tooltip = getStatusTooltip(status);
                      return (
                        <TableRow key={`${item.app_id}-${idx}`} hover>
                          {decomColumnVisibility.portfolio && <TableCell sx={{ fontSize: '13px' }}>{item.portfolio || 'N/A'}</TableCell>}
                          {decomColumnVisibility.platform && <TableCell sx={{ fontSize: '13px' }}>{item.platform || 'N/A'}</TableCell>}
                          {decomColumnVisibility.app_id && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.app_id
                                ? <Link href={`https://mac.maersk.io/application/${item.app_id}`} target="_blank" rel="noopener noreferrer" sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{item.app_id}</Link>
                                : 'N/A'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.app_name && <TableCell sx={{ fontSize: '13px' }}>{item.app_name || 'N/A'}</TableCell>}
                          {decomColumnVisibility.mvc_group && <TableCell sx={{ fontSize: '13px' }}>{item.mvc_group || 'N/A'}</TableCell>}
                          {decomColumnVisibility.owners && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.owner_names && item.owners
                                ? <MuiTooltip title={item.owners} arrow placement="top"><Box component="span" sx={{ cursor: 'help', borderBottom: '1px dashed #999' }}>{item.owner_names}</Box></MuiTooltip>
                                : item.owner_names || item.owners || 'N/A'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.target_date && (
                            <TableCell sx={{ fontSize: '13px' }}>{item.target_date_decom ? new Date(item.target_date_decom).toLocaleDateString() : 'N/A'}</TableCell>
                          )}
                          {decomColumnVisibility.prev_target_date && (
                            <TableCell sx={{ fontSize: '13px' }}>{(() => {
                              const allDates = getAllPrevTargetDates(item);
                              if (allDates.length === 0) return 'N/A';
                              if (allDates.length === 1) return allDates[0].toLocaleDateString();
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {allDates.map((d, i) => (
                                    <Box key={i} component="span" sx={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                                      {d.toLocaleDateString()}{i < allDates.length - 1 ? ' ;' : ''}
                                    </Box>
                                  ))}
                                </Box>
                              );
                            })()}</TableCell>
                          )}
                          {decomColumnVisibility.decom_date && (
                            <TableCell sx={{ fontSize: '13px' }}>{item.decom_date ? new Date(item.decom_date).toLocaleDateString() : 'N/A'}</TableCell>
                          )}
                          {decomColumnVisibility.delayed_comments && (
                            <TableCell sx={{ fontSize: '13px', maxWidth: 200 }}>
                              <MuiTooltip title={item.decom_extension_reason || ''} arrow placement="top">
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                                  {item.decom_extension_reason || 'N/A'}
                                </Box>
                              </MuiTooltip>
                            </TableCell>
                          )}
                          {decomColumnVisibility.strategic_intent && (
                            <TableCell sx={{ fontSize: '13px' }}>{item.strategic_intent?.intent || 'N/A'}</TableCell>
                          )}
                          {decomColumnVisibility.backup_tested && (() => {
                            const raw = item.backup_tested_last_12_months;
                            const isSpecial = raw && /^(OOS|NA|SAAS)$/i.test(raw.trim());
                            const restoreDate = raw && !isSpecial ? new Date(raw) : null;
                            const oneYearAgo = new Date(); oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                            const isRestoreOld = !restoreDate || restoreDate < oneYearAgo;
                            const restoreLabel = isSpecial ? raw!.trim().toUpperCase() : (restoreDate && !isNaN(restoreDate.getTime()) ? restoreDate.toLocaleDateString() : 'N/A');
                            const status = calculateDecommissioningStatus(item);
                            const highlightRestore = !isSpecial && status === 'Delayed' && isRestoreOld;
                            return (
                              <TableCell sx={{ fontSize: '13px' }}>
                                {highlightRestore ? (
                                  <Box component="span" sx={{ fontWeight: 600, color: '#f44336' }}>{restoreLabel}</Box>
                                ) : restoreLabel}
                              </TableCell>
                            );
                          })()}
                          {decomColumnVisibility.failover_tested && (() => {
                            const isDelayedRepeatedlyOrOverdue = status === 'Delayed';
                            const rawF = item.failover_tested_date_last_12_months;
                            const isSpecialF = rawF && /^(OOS|NA|SAAS)$/i.test(rawF.trim());
                            const failoverDate = rawF && !isSpecialF ? new Date(rawF) : null;
                            const oneYearAgo = new Date(); oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                            const isFailoverOld = !failoverDate || failoverDate < oneYearAgo;
                            const highlightFailover = !isSpecialF && isDelayedRepeatedlyOrOverdue && isFailoverOld;
                            const failoverLabel = isSpecialF ? rawF!.trim().toUpperCase() : (failoverDate && !isNaN(failoverDate.getTime()) ? failoverDate.toLocaleDateString() : 'N/A');
                            const failoverTooltip = failoverDate
                              ? `Last failover test was on ${failoverDate.toLocaleDateString()} — more than a year ago. Consider re-testing.`
                              : 'No failover test date recorded. This application is overdue for a failover test.';
                            return (
                              <TableCell sx={{ fontSize: '13px' }}>
                                {highlightFailover ? (
                                  <MuiTooltip title={failoverTooltip} arrow placement="top">
                                    <Box component="span" sx={{ cursor: 'help', fontWeight: 600, color: '#f44336' }}>
                                      {failoverLabel}
                                    </Box>
                                  </MuiTooltip>
                                ) : failoverLabel}
                              </TableCell>
                            );
                          })()}
                          {decomColumnVisibility.status && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              <Chip label={item.status || 'N/A'} size="small"
                                sx={{ bgcolor: item.status === 'Active' ? '#4caf50 !important' : '#9e9e9e !important', color: '#fff !important', fontWeight: 600, '& .MuiChip-label': { color: '#fff !important' } }} />
                            </TableCell>
                          )}
                          {decomColumnVisibility.decom_status && (
                            <TableCell sx={{ fontSize: '13px', padding: '8px !important', minWidth: '150px', maxWidth: '300px' }}>
                              <MuiTooltip title={`${tooltip}${item.decom_change_description ? `\n\nChange Description: ${item.decom_change_description}` : ''}`} arrow placement="top">
                                <Chip label={style.label} size="small"
                                  sx={{ bgcolor: `${style.bgcolor} !important`, color: `${style.color} !important`, fontWeight: 600, cursor: 'help', '& .MuiChip-label': { color: `${style.color} !important` } }} />
                              </MuiTooltip>
                            </TableCell>
                          )}
                          {decomColumnVisibility.application_labels && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {Array.isArray(item.application_labels) && item.application_labels.length
                                ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {item.application_labels.map((l: any, li: number) => (
                                    <MuiTooltip key={li} title={l.description || l.name} arrow placement="top">
                                      <Chip label={l.name} size="small" sx={{ bgcolor: `${l.color || '#666'} !important`, color: '#fff !important', fontSize: '0.7rem', height: '18px', '& .MuiChip-label': { color: '#fff !important', px: 0.8 } }} />
                                    </MuiTooltip>
                                  ))}
                                </Box>
                                : 'N/A'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.p1p2 && (
                            <TableCell sx={{ fontSize: '13px', textAlign: 'center' }}>
                              {(() => {
                                const counts = getIncidentCounts(item.app_id);
                                const total = counts.p1 + counts.p2;
                                return (
                                  <Chip
                                    label={total}
                                    size="small"
                                    color={total === 0 ? 'success' : 'error'}
                                    sx={{ fontSize: '11px' }}
                                  />
                                );
                              })()}
                            </TableCell>
                          )}
                          {decomColumnVisibility.p3p4mi && (
                            <TableCell sx={{ fontSize: '13px', textAlign: 'center' }}>
                              {(() => {
                                const counts = getIncidentCounts(item.app_id);
                                return (
                                  <Chip
                                    label={counts.p3p4mi}
                                    size="small"
                                    color={counts.p3p4mi === 0 ? 'success' : 'warning'}
                                    sx={{ fontSize: '11px' }}
                                  />
                                );
                              })()}
                            </TableCell>
                          )}
                          {decomColumnVisibility.tag && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {item.tag?.decomm_tag && (
                                  <MuiTooltip title={item.tag.remarks || item.tag.comments || 'No remarks'} arrow placement="top">
                                    <Chip label={item.tag.decomm_tag} size="small"
                                      sx={{ bgcolor: '#1976d2 !important', color: '#fff !important', fontSize: '0.75rem', height: '20px', cursor: 'pointer', '& .MuiChip-label': { color: '#fff !important', px: 1 } }} />
                                  </MuiTooltip>
                                )}
                                <IconButton size="small" sx={{ padding: '2px' }} title={item.tag ? 'Edit Tag' : 'Add Tag'}
                                  onClick={e => { e.stopPropagation(); item.tag ? handleEditTag(item) : handleAddTag(item); }}>
                                  {item.tag ? <EditIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Box>
                            </TableCell>
                          )}
                          {decomColumnVisibility.cip_direct_cost && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.cip_direct_cost != null ? (() => {
                                const n = typeof item.cip_direct_cost === 'string' ? parseFloat(item.cip_direct_cost as any) : item.cip_direct_cost;
                                if (isNaN(n)) return 'Not Available';
                                const amt = `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                return item.cip_month ? <MuiTooltip title={`CIP month: ${item.cip_month}`} arrow placement="top"><span>{amt}</span></MuiTooltip> : amt;
                              })() : 'Not Available'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.cip_indirect_cost && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.cip_indirect_cost != null ? (() => {
                                const n = typeof item.cip_indirect_cost === 'string' ? parseFloat(item.cip_indirect_cost as any) : item.cip_indirect_cost;
                                if (isNaN(n)) return 'Not Available';
                                const amt = `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                return item.cip_month ? <MuiTooltip title={`CIP month: ${item.cip_month}`} arrow placement="top"><span>{amt}</span></MuiTooltip> : amt;
                              })() : 'Not Available'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.total_cost && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.total_cost != null ? (() => {
                                const n = typeof item.total_cost === 'string' ? parseFloat(item.total_cost as any) : item.total_cost;
                                return !isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not Available';
                              })() : 'Not Available'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.bia_status && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.bia_status === true
                                ? <Chip label="BIA Valid" size="small" sx={{ fontSize: '11px', backgroundColor: '#d4edda !important', color: '#155724 !important', fontWeight: 500, border: '1px solid #c3e6cb' }} />
                                : item.bia_status === false
                                  ? <Chip label="BIA Expired" size="small" sx={{ fontSize: '11px', backgroundColor: '#f8d7da !important', color: '#721c24 !important', fontWeight: 500, border: '1px solid #f5c6cb' }} />
                                  : <Chip label="BIA - To be assessed" size="small" sx={{ fontSize: '11px', backgroundColor: '#fff3cd !important', color: '#856404 !important', fontWeight: 500, border: '1px solid #ffeeba' }} />}
                            </TableCell>
                          )}
                          {decomColumnVisibility.cyber_risk && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              {item.risk_exposure_all != null && Number(item.risk_exposure_all) > 0
                                ? <Chip label={item.risk_exposure_all} size="small" sx={{ fontSize: '11px', backgroundColor: '#f8d7da !important', color: '#721c24 !important', fontWeight: 600, border: '1px solid #f5c6cb' }} />
                                : item.risk_exposure_all != null
                                  ? <Chip label={item.risk_exposure_all} size="small" sx={{ fontSize: '11px', backgroundColor: '#d4edda !important', color: '#155724 !important', fontWeight: 500, border: '1px solid #c3e6cb' }} />
                                  : 'N/A'}
                            </TableCell>
                          )}
                          {decomColumnVisibility.suspended && (
                            <TableCell sx={{ fontSize: '13px' }}>
                              <Chip
                                label={item.suspended === true ? 'Yes' : item.suspended === false ? 'No' : '-'}
                                size="small"
                                sx={{
                                  bgcolor: item.suspended === true ? '#ed6c02 !important' : item.suspended === false ? '#9e9e9e !important' : 'transparent !important',
                                  color: item.suspended != null ? '#fff !important' : '#666 !important',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { color: 'inherit !important' },
                                }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={decomPage}
                onPageChange={(_, p) => setDecomPage(p)}
                rowsPerPage={decomRowsPerPage}
                onRowsPerPageChange={e => { setDecomRowsPerPage(parseInt(e.target.value, 10)); setDecomPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '13px' } }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* ─── Popovers ─────────────────────────────────────────────────────────── */}

      {/* Portfolio */}
      <CheckboxListPopover
        anchor={decomPortfolioFilterAnchor}
        onClose={() => { setDecomPortfolioFilterAnchor(null); setDecomPortfolioSearchText(''); }}
        title="Select Portfolio"
        searchText={decomPortfolioSearchText}
        onSearchChange={setDecomPortfolioSearchText}
        items={Array.from(new Set(decommissioningData.map(i => i.portfolio))).sort().filter(p => p.toLowerCase().includes(decomPortfolioSearchText.toLowerCase()))}
        selected={decomPortfolioTempFilter}
        onToggle={(v: string) => setDecomPortfolioTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomPortfolioTempFilter([])}
        onApply={() => { setDecomPortfolioFilter(decomPortfolioTempFilter); setDecomPortfolioFilterAnchor(null); setDecomPortfolioSearchText(''); setDecomPage(0); }}
      />

      {/* Platform */}
      <CheckboxListPopover
        anchor={decomPlatformFilterAnchor}
        onClose={() => { setDecomPlatformFilterAnchor(null); setDecomPlatformSearchText(''); }}
        title="Select Platform"
        searchText={decomPlatformSearchText}
        onSearchChange={setDecomPlatformSearchText}
        items={Array.from(new Set(decommissioningData.map(i => i.platform))).sort().filter(p => p.toLowerCase().includes(decomPlatformSearchText.toLowerCase()))}
        selected={decomPlatformTempFilter}
        onToggle={(v: string) => setDecomPlatformTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomPlatformTempFilter([])}
        onApply={() => { setDecomPlatformFilter(decomPlatformTempFilter); setDecomPlatformFilterAnchor(null); setDecomPlatformSearchText(''); setDecomPage(0); }}
      />

      {/* Strategic Intent */}
      <CheckboxListPopover
        anchor={decomStrategicIntentFilterAnchor}
        onClose={() => { setDecomStrategicIntentFilterAnchor(null); setDecomStrategicIntentSearchText(''); }}
        title="Select Strategic Intent"
        searchText={decomStrategicIntentSearchText}
        onSearchChange={setDecomStrategicIntentSearchText}
        items={Array.from(new Set(decommissioningData.map(i => i.strategic_intent?.intent || 'N/A'))).sort().filter(s => s.toLowerCase().includes(decomStrategicIntentSearchText.toLowerCase()))}
        selected={decomStrategicIntentTempFilter}
        onToggle={(v: string) => setDecomStrategicIntentTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomStrategicIntentTempFilter([])}
        onApply={() => { setDecomStrategicIntentFilter(decomStrategicIntentTempFilter); setDecomStrategicIntentFilterAnchor(null); setDecomStrategicIntentSearchText(''); setDecomPage(0); }}
      />

      {/* MAC Status */}
      <CheckboxListPopover
        anchor={decomStatusFilterAnchor}
        onClose={() => { setDecomStatusFilterAnchor(null); setDecomStatusSearchText(''); }}
        title="Select MAC Status"
        searchText={decomStatusSearchText}
        onSearchChange={setDecomStatusSearchText}
        items={Array.from(new Set(decommissioningData.map(i => i.status || 'N/A'))).sort().filter(s => s.toLowerCase().includes(decomStatusSearchText.toLowerCase()))}
        selected={decomStatusTempFilter}
        onToggle={(v: string) => setDecomStatusTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomStatusTempFilter([])}
        onApply={() => { setDecomStatusFilter(decomStatusTempFilter); setDecomStatusFilterAnchor(null); setDecomStatusSearchText(''); setDecomPage(0); }}
      />

      {/* Decom Status */}
      <CheckboxListPopover
        anchor={decomDecomStatusFilterAnchor}
        onClose={() => { setDecomDecomStatusFilterAnchor(null); setDecomDecomStatusSearchText(''); }}
        title="Select Decom Status"
        searchText={decomDecomStatusSearchText}
        onSearchChange={setDecomDecomStatusSearchText}
        items={['Decommissioned', 'To be Decommissioned', 'Delayed', 'Awaiting Decomm Confirmation', 'Cancelled'].filter(s => s.toLowerCase().includes(decomDecomStatusSearchText.toLowerCase()))}
        selected={decomDecomStatusTempFilter}
        onToggle={(v: string) => setDecomDecomStatusTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomDecomStatusTempFilter([])}
        onApply={() => { setDecomDecomStatusFilter(decomDecomStatusTempFilter); setDecomDecomStatusFilterAnchor(null); setDecomDecomStatusSearchText(''); setDecomPage(0); }}
      />

      {/* Application Labels */}
      <CheckboxListPopover
        anchor={decomApplicationLabelsFilterAnchor}
        onClose={() => { setDecomApplicationLabelsFilterAnchor(null); setDecomApplicationLabelsSearchText(''); }}
        title="Select Application Labels"
        searchText={decomApplicationLabelsSearchText}
        onSearchChange={setDecomApplicationLabelsSearchText}
        items={(() => {
          const all = new Set<string>();
          decommissioningData.forEach(i => { if (Array.isArray(i.application_labels) && i.application_labels.length) i.application_labels.forEach((l: any) => all.add(l.name)); });
          if (decommissioningData.some(i => !i.application_labels || i.application_labels.length === 0)) all.add('N/A');
          return Array.from(all).sort().filter(l => l.toLowerCase().includes(decomApplicationLabelsSearchText.toLowerCase()));
        })()}
        selected={decomApplicationLabelsTempFilter}
        onToggle={(v: string) => setDecomApplicationLabelsTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomApplicationLabelsTempFilter([])}
        onApply={() => { setDecomApplicationLabelsFilter(decomApplicationLabelsTempFilter); setDecomApplicationLabelsFilterAnchor(null); setDecomApplicationLabelsSearchText(''); setDecomPage(0); }}
      />

      {/* Suspended */}
      <CheckboxListPopover
        anchor={decomSuspendedFilterAnchor}
        onClose={() => setDecomSuspendedFilterAnchor(null)}
        title="Filter Suspended"
        searchText=""
        onSearchChange={() => { }}
        hideSearch
        items={Array.from(new Set(decommissioningData.map(i => i.suspended === true ? 'Yes' : i.suspended === false ? 'No' : 'N/A'))).sort()}
        selected={decomSuspendedTempFilter}
        onToggle={(v: string) => setDecomSuspendedTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomSuspendedTempFilter([])}
        onApply={() => { setDecomSuspendedFilter(decomSuspendedTempFilter); setDecomSuspendedFilterAnchor(null); setDecomPage(0); }}
      />

      {/* BIA Status */}
      <CheckboxListPopover
        anchor={decomBiaStatusFilterAnchor}
        onClose={() => setDecomBiaStatusFilterAnchor(null)}
        title="Filter BIA Status"
        searchText=""
        onSearchChange={() => { }}
        hideSearch
        items={['BIA Valid', 'BIA Expired', 'BIA - To be assessed']}
        selected={decomBiaStatusTempFilter}
        onToggle={(v: string) => setDecomBiaStatusTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomBiaStatusTempFilter([])}
        onApply={() => { setDecomBiaStatusFilter(decomBiaStatusTempFilter); setDecomBiaStatusFilterAnchor(null); setDecomPage(0); }}
      />

      {/* Owners */}
      <Popover open={Boolean(decomOwnersFilterAnchor)} anchorEl={decomOwnersFilterAnchor} onClose={() => setDecomOwnersFilterAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 280, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', p: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Filter by Owners</Typography>
        <TextField fullWidth size="small" placeholder="Search by name or email..." value={decomOwnersTempFilter} onChange={e => setDecomOwnersTempFilter(e.target.value)} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setDecomOwnersTempFilter('')} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0' }}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => { setDecomOwnersFilter(decomOwnersTempFilter); setDecomOwnersFilterAnchor(null); setDecomPage(0); }} sx={{ textTransform: 'none', bgcolor: '#0a3055' }}>Apply</Button>
        </Box>
      </Popover>

      {/* MVC Group */}
      <CheckboxListPopover
        anchor={decomMvcGroupFilterAnchor}
        onClose={() => setDecomMvcGroupFilterAnchor(null)}
        title="Filter MVC Group"
        searchText=""
        onSearchChange={() => { }}
        hideSearch={false}
        items={getAvailableMvcGroups}
        selected={decomMvcGroupTempFilter}
        onToggle={(v: string) => setDecomMvcGroupTempFilter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])}
        onClear={() => setDecomMvcGroupTempFilter([])}
        onApply={() => { setDecomMvcGroupFilter(decomMvcGroupTempFilter); setDecomMvcGroupFilterAnchor(null); setDecomPage(0); }}
      />

      {/* Delayed Comments */}
      <Popover open={Boolean(decomDelayedCommentsFilterAnchor)} anchorEl={decomDelayedCommentsFilterAnchor} onClose={() => setDecomDelayedCommentsFilterAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 280, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', p: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Filter by Delayed Comments</Typography>
        <TextField fullWidth size="small" placeholder="Search comments..." value={decomDelayedCommentsTempFilter} onChange={e => setDecomDelayedCommentsTempFilter(e.target.value)} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setDecomDelayedCommentsTempFilter('')} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0' }}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => { setDecomDelayedCommentsFilter(decomDelayedCommentsTempFilter); setDecomDelayedCommentsFilterAnchor(null); setDecomPage(0); }} sx={{ textTransform: 'none', bgcolor: '#0a3055' }}>Apply</Button>
        </Box>
      </Popover>

      {/* Year filter */}
      <Popover open={Boolean(decomYearFilterAnchor)} anchorEl={decomYearFilterAnchor}
        onClose={() => { setDecomYearFilterAnchor(null); setAllYearsExpanded(false); setCustomRangeExpanded(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 240, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxHeight: 480, overflow: 'hidden' } }}>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>Filter by Year</Typography>
          {(allYearsExpanded || customRangeExpanded) && (
            <Button size="small" startIcon={<KeyboardArrowUp sx={{ transform: 'rotate(-90deg)' }} />}
              onClick={() => { setAllYearsExpanded(false); setCustomRangeExpanded(false); }}
              sx={{ textTransform: 'none', color: '#666', mb: 1.5, px: 0 }}>Back</Button>
          )}
          {!allYearsExpanded && !customRangeExpanded ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {[{ val: previousYear.toString(), label: `Previous Year (${previousYear})` }, { val: currentYear.toString(), label: `Current Year (${currentYear})` }, { val: nextYear.toString(), label: `Next Year (${nextYear})` }].map(({ val, label }) => (
                <Button key={val} fullWidth variant={decomYearFilter === val ? 'contained' : 'outlined'} size="small"
                  onClick={() => { setDecomYearFilter(val); setDecomPage(0); setDecomYearFilterAnchor(null); setAllYearsExpanded(false); setCustomRangeExpanded(false); }}
                  sx={{ textTransform: 'none', justifyContent: 'flex-start', bgcolor: decomYearFilter === val ? '#1976d2 !important' : 'transparent', color: decomYearFilter === val ? '#fff !important' : '#333' }}>
                  {label}
                </Button>
              ))}
              <Button fullWidth variant={decomYearFilter === 'custom' ? 'contained' : 'outlined'} size="small"
                onClick={() => setCustomRangeExpanded(true)}
                endIcon={<KeyboardArrowUp sx={{ transform: 'rotate(90deg)' }} />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', bgcolor: decomYearFilter === 'custom' ? '#1976d2 !important' : 'transparent', color: decomYearFilter === 'custom' ? '#fff !important' : '#333' }}>
                Custom Range
              </Button>
              <Button fullWidth variant={decomYearFilter === 'all' ? 'contained' : 'outlined'} size="small"
                onClick={() => { setAllYearsExpanded(true); }}
                endIcon={<KeyboardArrowUp sx={{ transform: 'rotate(90deg)' }} />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', bgcolor: decomYearFilter === 'all' ? '#1976d2 !important' : 'transparent', color: decomYearFilter === 'all' ? '#fff !important' : '#333' }}>
                All Years
              </Button>
            </Box>
          ) : customRangeExpanded ? (
            <Box>
              <TextField label="Start Date" type="date" size="small" fullWidth value={customRangeStart} onChange={e => setCustomRangeStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
              <TextField label="End Date" type="date" size="small" fullWidth value={customRangeEnd} onChange={e => setCustomRangeEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setCustomRangeStart(''); setCustomRangeEnd(''); }} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0' }}>Clear</Button>
                <Button size="small" variant="contained" disabled={!customRangeStart || !customRangeEnd}
                  onClick={() => { setDecomYearFilter('custom'); setDecomPage(0); setDecomYearFilterAnchor(null); setCustomRangeExpanded(false); }}
                  sx={{ textTransform: 'none' }}>Apply</Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <TextField size="small" placeholder="Search years..." value={yearSearchText} onChange={e => setYearSearchText(e.target.value)} fullWidth sx={{ mb: 1 }}
                InputProps={{ startAdornment: <Search sx={{ color: '#aaa', mr: 0.5, fontSize: 18 }} /> }} />
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button size="small" onClick={() => setSelectedYears(getAvailableYears.map((y: number) => y.toString()))} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Select All</Button>
                <Button size="small" onClick={() => setSelectedYears([])} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Clear All</Button>
              </Box>
              <Box sx={{ maxHeight: 160, overflowY: 'auto', mb: 1 }}>
                {getAvailableYears.filter((y: number) => y.toString().includes(yearSearchText)).map((year: number) => (
                  <FormControlLabel key={year}
                    control={<Checkbox checked={selectedYears.includes(year.toString())} onChange={e => setSelectedYears(p => e.target.checked ? [...p, year.toString()] : p.filter(y2 => y2 !== year.toString()))} size="small" />}
                    label={year.toString()} sx={{ display: 'block' }} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="contained" onClick={() => { setDecomYearFilter('all'); setDecomPage(0); setDecomYearFilterAnchor(null); setAllYearsExpanded(false); }} sx={{ textTransform: 'none' }}>Apply</Button>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>

      {/* Prev Target Date */}
      <Popover open={Boolean(decomPrevTargetDateFilterAnchor)} anchorEl={decomPrevTargetDateFilterAnchor}
        onClose={() => { setDecomPrevTargetDateFilterAnchor(null); setDecomPrevTddYearSearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 320, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}>
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={decomPrevTargetDateTempOperator} onChange={e => setDecomPrevTargetDateTempOperator(e.target.value as any)}
              sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' } }}>
              <MenuItem value="equals">Equals</MenuItem>
              <MenuItem value="after">After</MenuItem>
              <MenuItem value="before">Before</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
              <MenuItem value="allyears">All Years</MenuItem>
            </Select>
          </FormControl>
          {decomPrevTargetDateTempOperator === 'allyears' ? (
            <Box>
              <TextField size="small" placeholder="Search years..." value={decomPrevTddYearSearch}
                onChange={e => setDecomPrevTddYearSearch(e.target.value)} fullWidth sx={{ mb: 1 }}
                InputProps={{ startAdornment: <Search sx={{ color: '#aaa', mr: 0.5, fontSize: 18 }} /> }} />
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button size="small" onClick={() => setDecomPrevTddTempSelectedYears(getAvailablePrevTddYears.map(y => y.toString()))} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Select All</Button>
                <Button size="small" onClick={() => setDecomPrevTddTempSelectedYears([])} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Clear All</Button>
              </Box>
              <Box sx={{ maxHeight: 160, overflowY: 'auto', mb: 1 }}>
                {getAvailablePrevTddYears.filter(y => y.toString().includes(decomPrevTddYearSearch)).map(year => (
                  <FormControlLabel key={year}
                    control={<Checkbox checked={decomPrevTddTempSelectedYears.includes(year.toString())}
                      onChange={e => setDecomPrevTddTempSelectedYears(p => e.target.checked ? [...p, year.toString()] : p.filter(y2 => y2 !== year.toString()))}
                      size="small" />}
                    label={year.toString()} sx={{ display: 'block' }} />
                ))}
              </Box>
            </Box>
          ) : decomPrevTargetDateTempOperator === 'custom' ? (
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>Start Date</Typography>
              <TextField fullWidth size="small" type="date" value={decomPrevTargetDateTempCustomStart} onChange={e => setDecomPrevTargetDateTempCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>End Date</Typography>
              <TextField fullWidth size="small" type="date" value={decomPrevTargetDateTempCustomEnd} onChange={e => setDecomPrevTargetDateTempCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
            </Box>
          ) : (
            <TextField fullWidth size="small" type="date" value={decomPrevTargetDateTempFilter} onChange={e => setDecomPrevTargetDateTempFilter(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2, '& fieldset': { borderColor: '#1976d2' } }} />
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => {
              setDecomPrevTargetDateTempFilter('');
              setDecomPrevTargetDateTempCustomStart(''); setDecomPrevTargetDateTempCustomEnd('');
              setDecomPrevTddTempSelectedYears([]);
            }} sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0' }}>Clear</Button>
            <Button size="small" variant="contained" onClick={() => {
              setDecomPrevTargetDateFilter(decomPrevTargetDateTempFilter);
              setDecomPrevTargetDateOperator(decomPrevTargetDateTempOperator);
              setDecomPrevTargetDateCustomStart(decomPrevTargetDateTempCustomStart);
              setDecomPrevTargetDateCustomEnd(decomPrevTargetDateTempCustomEnd);
              setDecomPrevTddSelectedYears(decomPrevTddTempSelectedYears);
              setDecomPrevTargetDateFilterAnchor(null); setDecomPage(0);
            }} sx={{ textTransform: 'none', bgcolor: '#0a3055' }}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      {/* Decom Date */}
      <DateFilterPopover
        anchor={decomDecomDateFilterAnchor} onClose={() => setDecomDecomDateFilterAnchor(null)}
        tempOperator={decomDecomDateTempOperator} setTempOperator={setDecomDecomDateTempOperator}
        tempFilter={decomDecomDateTempFilter} setTempFilter={setDecomDecomDateTempFilter}
        tempCustomStart={decomDecomDateTempCustomStart} setTempCustomStart={setDecomDecomDateTempCustomStart}
        tempCustomEnd={decomDecomDateTempCustomEnd} setTempCustomEnd={setDecomDecomDateTempCustomEnd}
        onClear={() => { setDecomDecomDateTempFilter(''); setDecomDecomDateTempCustomStart(''); setDecomDecomDateTempCustomEnd(''); }}
        onApply={() => { setDecomDecomDateFilter(decomDecomDateTempFilter); setDecomDecomDateOperator(decomDecomDateTempOperator); setDecomDecomDateCustomStart(decomDecomDateTempCustomStart); setDecomDecomDateCustomEnd(decomDecomDateTempCustomEnd); setDecomDecomDateFilterAnchor(null); setDecomPage(0); }}
      />

      {/* Failover Tested Date */}
      <DateFilterPopover
        anchor={decomFailoverTestedDateFilterAnchor} onClose={() => setDecomFailoverTestedDateFilterAnchor(null)}
        tempOperator={decomFailoverTestedDateTempOperator} setTempOperator={setDecomFailoverTestedDateTempOperator}
        tempFilter={decomFailoverTestedDateTempFilter} setTempFilter={setDecomFailoverTestedDateTempFilter}
        tempCustomStart={decomFailoverTestedDateTempCustomStart} setTempCustomStart={setDecomFailoverTestedDateTempCustomStart}
        tempCustomEnd={decomFailoverTestedDateTempCustomEnd} setTempCustomEnd={setDecomFailoverTestedDateTempCustomEnd}
        onClear={() => { setDecomFailoverTestedDateTempFilter(''); setDecomFailoverTestedDateTempCustomStart(''); setDecomFailoverTestedDateTempCustomEnd(''); }}
        onApply={() => { setDecomFailoverTestedDateFilter(decomFailoverTestedDateTempFilter); setDecomFailoverTestedDateOperator(decomFailoverTestedDateTempOperator); setDecomFailoverTestedDateCustomStart(decomFailoverTestedDateTempCustomStart); setDecomFailoverTestedDateCustomEnd(decomFailoverTestedDateTempCustomEnd); setDecomFailoverTestedDateFilterAnchor(null); setDecomPage(0); }}
      />

      {/* Backup Tested Date */}
      <DateFilterPopover
        anchor={decomBackupTestedDateFilterAnchor} onClose={() => setDecomBackupTestedDateFilterAnchor(null)}
        tempOperator={decomBackupTestedDateTempOperator} setTempOperator={setDecomBackupTestedDateTempOperator}
        tempFilter={decomBackupTestedDateTempFilter} setTempFilter={setDecomBackupTestedDateTempFilter}
        tempCustomStart={decomBackupTestedDateTempCustomStart} setTempCustomStart={setDecomBackupTestedDateTempCustomStart}
        tempCustomEnd={decomBackupTestedDateTempCustomEnd} setTempCustomEnd={setDecomBackupTestedDateTempCustomEnd}
        onClear={() => { setDecomBackupTestedDateTempFilter(''); setDecomBackupTestedDateTempCustomStart(''); setDecomBackupTestedDateTempCustomEnd(''); }}
        onApply={() => { setDecomBackupTestedDateFilter(decomBackupTestedDateTempFilter); setDecomBackupTestedDateOperator(decomBackupTestedDateTempOperator); setDecomBackupTestedDateCustomStart(decomBackupTestedDateTempCustomStart); setDecomBackupTestedDateCustomEnd(decomBackupTestedDateTempCustomEnd); setDecomBackupTestedDateFilterAnchor(null); setDecomPage(0); }}
      />

      {/* Column Settings Dialog */}
      <Dialog open={decomColumnDialogOpen} onClose={() => setDecomColumnDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings sx={{ color: '#666' }} /> Column Settings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>Select which columns to display:</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {([
              ['portfolio', 'Portfolio'], ['platform', 'Platform'], ['app_id', 'App ID'], ['app_name', 'App Name'],
              ['mvc_group', 'MVC Group'], ['owners', 'Owners'], ['target_date', 'Target Decom Date'],
              ['prev_target_date', 'Prev Target Date'], ['decom_date', 'Decom Date'],
              ['delayed_comments', 'Delayed Comments from Mac'], ['strategic_intent', 'Strategic Intent'],
              ['backup_tested', 'Restore from Backup Tested'], ['failover_tested', 'Failover Tested Date'],
              ['status', 'MAC Status'], ['decom_status', 'Decom Status'], ['tag', 'Tag'],
              ['application_labels', 'Application Labels'], ['p1p2', 'P1/P2 Incidents'],
              ['p3p4mi', 'P3/P4 Incidents'], ['cip_direct_cost', 'CIP Direct Cost'], ['cip_indirect_cost', 'CIP Indirect Cost'], ['total_cost', 'Total Approx Cost'], ['bia_status', 'BIA Status'], ['cyber_risk', 'Cyber Risk Exposure'], ['suspended', 'Suspended'],
            ] as [keyof typeof decomColumnVisibility, string][]).map(([key, label]) => (
              <FormControlLabel key={key}
                control={<Checkbox checked={decomColumnVisibility[key]} onChange={e => setDecomColumnVisibility(p => ({ ...p, [key]: e.target.checked }))} size="small" />}
                label={<Typography variant="body2">{label}</Typography>} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDecomColumnVisibility({ portfolio: true, platform: true, app_id: true, app_name: true, mvc_group: false, owners: true, target_date: true, prev_target_date: true, decom_date: true, delayed_comments: false, strategic_intent: false, backup_tested: false, failover_tested: false, status: true, decom_status: true, tag: false, application_labels: false, p1p2: false, p3p4mi: false, cip_direct_cost: false, cip_indirect_cost: false, total_cost: false, bia_status: false, cyber_risk: false, suspended: false }); }} sx={{ mr: 'auto', textTransform: 'none' }}>Reset to Defaults</Button>
          <Button onClick={() => setDecomColumnDialogOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Top-centered snackbar — only shown after operation completes */}
      <Snackbar
        open={!!tagSuccessMessage}
        autoHideDuration={2500}
        onClose={() => { setTagSuccessMessage(''); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: { xs: 16, sm: 24 },
          '& .MuiPaper-root': { bgcolor: '#d4edda', color: '#0f5132', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', px: 2, minWidth: 280, fontWeight: 500 }
        }}
        message={tagSuccessMessage}
        action={
          lastDeletedTag ? (
            <Button color="secondary" size="small" onClick={async () => {
              // Undo action: recreate the tag via POST (creates a 'tag added' row)
              const saved = lastDeletedTag;

              if (!saved) return;
              const { appId, tag } = saved;
              try {
                const payload = {
                  app_id: appId,
                  app_name: tag?.app_name || '',
                  decomm_tag: tag?.decomm_tag || '',
                  remarks: tag?.comments || tag?.remarks || '',
                  created_by: currentUserEmail,
                  updated_by: currentUserEmail,
                  status: 'tag added'
                };
                const r = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const res = await r.json();
                setDecommissioningData(prev => prev.map(item => item.app_id === appId ? { ...item, tag: { id: res.data?.id || Date.now(), decomm_tag: payload.decomm_tag, remarks: payload.remarks, comments: payload.remarks, created_by: payload.created_by, updated_by: payload.updated_by, created_at: res.data?.created_at || new Date().toISOString(), updated_at: res.data?.created_at || new Date().toISOString() } } : item));
                setTagSuccessMessage('Tag restored');
              } catch (e) {
                alert('Failed to restore tag.');
              } finally {
                setLastDeletedTag(null);
              }
            }}>Undo</Button>
          ) : null
        }
      />
    </Box>
  );
  
};
export default DecommissioningStatus;


